import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGigs } from '../hooks/useGigs';
import { useMusicTracks } from '../hooks/useMusicTracks';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { supabase } from '../lib/supabaseClient';
import { calculateGigTimes }        from '../lib/gigRouting';
import { useScheduleNotifications } from '../hooks/useScheduleNotifications';
import { useNotificationPrefs }     from '../hooks/useNotificationPrefs';
import { safeUrl, icsEscape } from '../utils/url';
import {
  DateInput,
  TimeInput,
  TimezoneInput,
  TIMEZONES,
} from '../components/FormInputs';

// ============================================================
// gig.js - Contains THREE components in one file:
//   1. GigList    - Shows all gigs with filter tabs
//   2. GigDetails - Shows one gig's full info + setlist player
//   3. AddGig     - Form to create OR edit a gig
//
// ── HIDE EARNINGS (GigList only) ─────────────────────────────
//   GigList reads user_metadata.hide_earnings from Supabase auth.
//   When true:
//     - $gig.payment is hidden from every gig card in the list
//     - The "Gig Total" sort button is removed from the sort bar
//   GigDetails and AddGig are unaffected; fee fields are always
//   visible on the detail and edit pages.
//
// ── NOTIFICATION CHANGE ───────────────────────────────────────
// AddGig now calls checkPendingNotifications() immediately after
// scheduleForGig() on every save. This mirrors the vanilla capstone
// where add-gig.js calls both in sequence:
//
//   await scheduleGigNotifications(gigId, gigData, userId)
//   await checkPendingNotifications()
//
// scheduleForGig writes rows to scheduled_notifications, setting
// send_at = now for any times already in the past. The immediate
// checkPendingNotifications call then fires those rows right away
// as browser notifications via the service worker.
//
// ── GETTING READY: TWO-STEP COMPOUND VALUE SYSTEM ────────────
//
// Step 1: Where are you starting from? (readyStep1 state)
//   'home'   Starting from home.
//   'other'  Starting from another location (hotel, friend's, studio).
//   'venue'  Driving to the venue first, getting ready on arrival.
//
// Step 2: How much prep happens at that location? (readySplit state)
//   'full'   Fully ready before leaving.
//   'half'   50% prep there, 50% at the venue.
//   'most'   70% prep there, last 30% at venue.
//
// Combined compound strings written to Supabase:
//   home  + full -> "home"
//   home  + half -> "home_half"
//   home  + most -> "home_most"
//   other + full -> "other"
//   other + half -> "other_half"
//   other + most -> "other_most"
//   venue        -> "venue"
// ============================================================


// ── CONSTANTS ─────────────────────────────────────────────────

const NOMINATIM_USER_AGENT = 'DragPocketPlanner/1.0 (https://drag-helper.netlify.app)'

// ── Contract upload and view (managed gigs) ──────────────────
// The "contracts" Storage bucket is public: public reads, authenticated
// inserts. That means getPublicUrl() is enough for both managers and
// performers to view a file, no signed URL call is needed here.
const CONTRACT_ALLOWED_EXT  = ['pdf', 'doc', 'docx']
const CONTRACT_ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

// The file input's accept attribute is only a hint, some OS file dialogs
// ignore it, so both the extension and the MIME type get checked here
// before anything is uploaded.
function isAllowedContractFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase()
  return CONTRACT_ALLOWED_EXT.includes(ext) && CONTRACT_ALLOWED_MIME.includes(file.type)
}

// Uploads a contract file to the shared "contracts" bucket and returns
// its public URL. Path is namespaced by user id, then gig id plus a
// timestamp, so a re-upload never collides with or silently reuses an
// older file's path.
async function uploadContractFile(gigId, userId, file) {
  const ext  = (file.name.split('.').pop() || 'pdf').toLowerCase()
  const path = `${userId}/${gigId}-${Date.now()}.${ext}`
  const { error: uploadError } = await supabase.storage
    .from('contracts')
    .upload(path, file, { upsert: true })
  if (uploadError) throw uploadError
  const { data } = supabase.storage.from('contracts').getPublicUrl(path)
  return data.publicUrl
}


// ── HELPERS ───────────────────────────────────────────────────

function parseCityStateFromAddress(address) {
  if (!address) return { city: '', state: '' }
  const match = address.match(/,\s*([^,]+),\s*([A-Z]{2})\s*\d{0,5}\s*$/i)
  if (match) {
    return {
      city:  match[1].trim(),
      state: match[2].trim().toUpperCase(),
    }
  }
  return { city: '', state: '' }
}

const mapsUrl = (address) => {
  if (!address || !address.trim()) return null
  return `https://maps.google.com/?q=${encodeURIComponent(address.trim())}`
}

const fmtShowTime = (timeStr) => {
  if (!timeStr) return ''
  const parts = timeStr.split(':').map(Number)
  if (parts.length < 2 || isNaN(parts[0])) return timeStr
  let h = parts[0]
  const m = String(parts[1] ?? 0).padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'
  if (h === 0)     h = 12
  else if (h > 12) h -= 12
  return `${h}:${m} ${ampm}`
}

function buildAudienceEventData(gig) {
  const title         = (gig.name || gig.venue_name || 'Show').trim()
  const locationParts = [gig.venue_name, gig.venue_address].filter(Boolean)
  const location      = locationParts.join(', ')
  const descLines     = []
  if (gig.age_restriction) descLines.push(`Age: ${gig.age_restriction}`)
  if (gig.cover_charge)    descLines.push(`Cover: ${gig.cover_charge}`)
  const [year, month, day] = gig.date.split('T')[0].split('-').map(Number)
  const timeParts = (gig.time || '20:00').split(':').map(Number)
  const hours   = timeParts[0] ?? 20
  const minutes = timeParts[1] ?? 0
  const pad     = (n) => String(n).padStart(2, '0')
  const dtStart = `${year}${pad(month)}${pad(day)}T${pad(hours)}${pad(minutes)}00`
  const endDate = new Date(year, month - 1, day, hours + 2, minutes)
  const dtEnd   = `${endDate.getFullYear()}${pad(endDate.getMonth() + 1)}${pad(endDate.getDate())}T${pad(endDate.getHours())}${pad(endDate.getMinutes())}00`
  const tzid    = gig.timezone || 'America/New_York'
  return { title, location, descLines, dtStart, dtEnd, tzid }
}

// ── COMPOUND VALUE HELPERS ────────────────────────────────────

function buildCompoundReadyLocation(step1, split) {
  if (step1 === 'venue') return 'venue'
  if (split === 'full')  return step1
  return `${step1}_${split}`
}

function parseCompoundReadyLocation(compound) {
  if (!compound || compound === 'home') return { step1: 'home',  split: 'full' }
  if (compound === 'venue')             return { step1: 'venue', split: 'full' }
  if (compound === 'other')             return { step1: 'other', split: 'full' }
  if (compound === 'home_half')         return { step1: 'home',  split: 'half' }
  if (compound === 'home_most')         return { step1: 'home',  split: 'most' }
  if (compound === 'other_half')        return { step1: 'other', split: 'half' }
  if (compound === 'other_most')        return { step1: 'other', split: 'most' }
  if (compound === 'half') return { step1: 'home',  split: 'half' }
  if (compound === 'most') return { step1: 'home',  split: 'most' }
  return { step1: 'home', split: 'full' }
}

function getReadyLabel(compound) {
  switch (compound) {
    case 'home':       return 'At home, fully ready before leaving'
    case 'home_half':  return 'At home, half prep there and half at venue'
    case 'home_most':  return 'At home, mostly ready and finishing at venue'
    case 'venue':      return 'At the venue, arriving first and getting ready there'
    case 'other':      return 'At another location, fully ready before leaving'
    case 'other_half': return 'At another location, half prep there and half at venue'
    case 'other_most': return 'At another location, mostly ready and finishing at venue'
    default:           return compound
  }
}

function calcFee(payment, feeType, feePct, feeFlat) {
  const base = parseFloat(payment) || 0
  if (!base) return 0
  if (feeType === 'flat') return parseFloat(feeFlat) || 0
  return base * ((parseFloat(feePct) || 0) / 100)
}

function feeLabel(feeType, feePct, feeFlat) {
  if (feeType === 'flat') return `$${parseFloat(feeFlat || 0).toFixed(0)} flat`
  return `${parseFloat(feePct || 0)}%`
}

// tzFromOpenMeteo -- queries Open-Meteo's free geocoding API to resolve
// an IANA timezone from a city + state. No API key, no signup, CORS-enabled.
// Uses admin1_code (state abbreviation) to disambiguate cities that appear
// in multiple states (e.g. "Springfield" in IL, MO, OH, etc.).
async function tzFromOpenMeteo(city, stateRaw) {
  if (!city) return null
  try {
    // Expand all common US city name abbreviations that Nominatim returns.
    // GeoNames (which Open-Meteo uses) stores full unabbreviated names,
    // so "St Paul" must become "Saint Paul" to match.
    //
    // Prefixes Nominatim commonly abbreviates:
    //   St. / St   -> Saint      (St Paul, St Louis, St Cloud)
    //   Ste. / Ste -> Sainte     (Ste Genevieve)
    //   Mt. / Mt   -> Mount      (Mt Vernon, Mt Pleasant, Mt Olive)
    //   Ft. / Ft   -> Fort       (Ft Worth, Ft Collins, Ft Wayne)
    //   Pt. / Pt   -> Point      (Pt Pleasant)
    //   E. / E     -> East       (E St Louis, E Chicago)
    //   W. / W     -> West       (W Palm Beach, W New York)
    //   N. / N     -> North      (N Las Vegas, N Little Rock)
    //   S. / S     -> South      (S Bend, S Gate)
    //   Lk. / Lk   -> Lake       (Lk Charles -- rare but exists)
    //   Jct. / Jct -> Junction   (very rare in city names)
    //
    // The \b word boundary prevents "Street" matching "S." prefix rules.
    // Each replace only fires if the abbreviation appears at the START
    // of the city name (^ anchor) to avoid mangling names like "West End".
    const expanded = city
      .replace(/^St\.?\s+/i,  'Saint ')
      .replace(/^Ste\.?\s+/i, 'Sainte ')
      .replace(/^Mt\.?\s+/i,  'Mount ')
      .replace(/^Ft\.?\s+/i,  'Fort ')
      .replace(/^Pt\.?\s+/i,  'Point ')
      .replace(/^E\.?\s+/i,   'East ')
      .replace(/^W\.?\s+/i,   'West ')
      .replace(/^N\.?\s+/i,   'North ')
      .replace(/^S\.?\s+/i,   'South ')
      .replace(/^Lk\.?\s+/i,  'Lake ')
      .trim()

    const res  = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(expanded)}&count=10&language=en&format=json&countryCode=US`
    )
    const data = await res.json()
    if (!data.results?.length) return null

    const abbrev = stateRaw ? normaliseState(stateRaw) : null

    const match = abbrev
      ? data.results.find(r => {
          const code = (r.admin1_code || '').replace(/^US-/, '')
          return code === abbrev
        }) || data.results[0]
      : data.results[0]

    return match?.timezone || null
  } catch {
    return null
  }
}


// ============================================================
// GigList
// ============================================================

const STATE_ABBREV = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
  CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',
  MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',
  ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',
  RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',
  TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'District of Columbia',
}

const STATE_FULL_TO_ABBREV = Object.fromEntries(
  Object.entries(STATE_ABBREV).map(([abbrev, full]) => [full.toLowerCase(), abbrev])
)

function normaliseState(raw) {
  if (!raw) return null
  const trimmed = raw.trim()
  const upper   = trimmed.toUpperCase()
  if (STATE_ABBREV[upper]) return upper
  const abbrev = STATE_FULL_TO_ABBREV[trimmed.toLowerCase()]
  if (abbrev)  return abbrev
  return upper.length <= 3 ? upper : null
}

function parseHomeLocation(locationStr) {
  if (!locationStr) return null
  const parts = locationStr.split(',').map(s => s.trim())
  const city  = parts[0]?.toLowerCase() || ''
  const state = normaliseState(parts[1] || '')?.toLowerCase() || ''
  if (!city) return null
  return { city, state }
}

function isHomeGig(gig, homeLocation) {
  if (!homeLocation) return null
  const gigCity  = (gig.city  || '').toLowerCase().trim()
  const gigState = normaliseState(gig.state || '')?.toLowerCase() || ''
  const cityMatch  = gigCity  && gigCity  === homeLocation.city
  const stateMatch = gigState && gigState === homeLocation.state
  if (!cityMatch) return false
  if (homeLocation.state && gigState && !stateMatch) return false
  return true
}

export function GigList() {

  const { gigs, loading, updateGig } = useGigs()
  // gigs = all gigs for this user loaded from Supabase
  const navigate          = useNavigate()

  const [hideEarnings, setHideEarnings] = useState(false)
  // reads user_metadata.hide_earnings
  // when true, $payment is hidden on every card in this list

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setHideEarnings(!!user.user_metadata?.hide_earnings)
    })
  }, [])

  // ── Pending gig management requests ──────────────────────────
  // Fetches the set of gig IDs that have an open management request
  // so GigList can show a "Request Pending" badge on those cards.
  const [pendingReqGigIds, setPendingReqGigIds] = useState(new Set())
  // Set() is used instead of an array for fast .has() lookups per card

  useEffect(() => {
    async function loadPendingReqs() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { data } = await supabase
        .from('management_requests')
        .select('gig_id')
        .eq('performer_id', user.id)
        .eq('status', 'pending')
      if (data) setPendingReqGigIds(new Set(data.map(r => r.gig_id)))
    }
    loadPendingReqs()
  }, [])

  const STORAGE_KEY = 'gigListFilters'
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.toString() === '') {
      const saved = sessionStorage.getItem(STORAGE_KEY)
      if (saved) {
        setSearchParams(new URLSearchParams(saved), { replace: true })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, searchParams.toString())
  }, [searchParams])

  const filter        = searchParams.get('tab')   ?? 'upcoming'
  const locationTab   = searchParams.get('loc')   ?? 'all'
  const selectedYear  = searchParams.get('year')  ?? 'all'
  const selectedMonth = searchParams.get('month') ?? 'all'
  const selectedVenue = searchParams.get('venue') ?? 'all'
  const selectedCity  = searchParams.get('city')  ?? 'all'
  const searchTerm    = searchParams.get('q')     ?? ''

  const setParam = (key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === '' || value === null || value === undefined) {
        next.delete(key)
      } else {
        next.set(key, value)
      }
      return next
    }, { replace: true })
  }

  const [homeLocation, setHomeLocation] = useState(undefined)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const raw = user?.user_metadata?.location || ''
      setHomeLocation(parseHomeLocation(raw))
    })
  }, [])

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const parseGigDate = (dateStr) => {
    const parts = dateStr.split('T')[0].split('-')
    return new Date(parts[0], parts[1] - 1, parts[2])
  }

  const formatDate = (dateStr) =>
    parseGigDate(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    })

  const tabGigs = useMemo(() => {
    return gigs.filter(gig => {
      const d = parseGigDate(gig.date)
      d.setHours(0, 0, 0, 0)
      if (filter === 'upcoming') return d >= today
      if (filter === 'past')     return d <  today
      return true
    })
  }, [gigs, filter, today])

  const availableYears = useMemo(() => {
    const years = tabGigs.map(g => parseGigDate(g.date).getFullYear())
    return [...new Set(years)].sort((a, b) => b - a)
  }, [tabGigs])

  const availableMonths = useMemo(() => {
    const pool = selectedYear === 'all'
      ? tabGigs
      : tabGigs.filter(g => parseGigDate(g.date).getFullYear() === parseInt(selectedYear))
    const months = pool.map(g => parseGigDate(g.date).getMonth())
    return [...new Set(months)].sort((a, b) => a - b)
  }, [tabGigs, selectedYear])

  const availableVenues = useMemo(() => {
    const venues = gigs.map(g => g.venue_name || '').filter(Boolean)
    return [...new Set(venues)].sort((a, b) => a.localeCompare(b))
  }, [gigs])

  const availableCities = useMemo(() => {
    const cities = gigs.map(g => g.city || '').filter(Boolean)
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b))
  }, [gigs])

  const monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ]

  const sortField = searchParams.get('sort') ?? 'date'
  const sortDir   = searchParams.get('dir')  ?? (sortField === 'date' ? 'asc' : 'desc')

  const handleSort = (field) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (sortField === field) {
        next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
      } else {
        next.set('sort', field)
        next.set('dir', field === 'date' ? 'asc' : 'desc')
      }
      return next
    }, { replace: true })
  }

  // Returns the correct sort icon element for a given column field.
  const sortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up gig-sort-icon"></i>
    }
    return sortDir === 'asc'
      ? <i className="bi bi-arrow-up gig-sort-icon--active"></i>
      : <i className="bi bi-arrow-down gig-sort-icon--active"></i>
  }

  const filteredGigs = useMemo(() => {
    const filtered = gigs.filter(gig => {
      const gigDate = parseGigDate(gig.date)
      gigDate.setHours(0, 0, 0, 0)

      if (filter === 'upcoming' && gigDate < today)  return false
      if (filter === 'past'     && gigDate >= today) return false
      if (selectedYear !== 'all' && gigDate.getFullYear() !== parseInt(selectedYear)) return false
      if (selectedMonth !== 'all' && gigDate.getMonth() !== parseInt(selectedMonth)) return false
      if (selectedVenue !== 'all' && (gig.venue_name || '') !== selectedVenue) return false
      if (selectedCity !== 'all' && (gig.city || '') !== selectedCity) return false

      if (locationTab !== 'all' && homeLocation !== undefined) {
        const home = isHomeGig(gig, homeLocation)
        if (locationTab === 'home' && !home) return false
        if (locationTab === 'away' &&  home) return false
      }

      if (searchTerm.trim()) {
        const haystack = (gig.name || gig.venue_name || '').toLowerCase()
        if (!haystack.includes(searchTerm.toLowerCase())) return false
      }

      return true
    })

    return [...filtered].sort((a, b) => {
      let aVal, bVal
      if (sortField === 'date') {
        aVal = parseGigDate(a.date).getTime()
        bVal = parseGigDate(b.date).getTime()
      } else {
        aVal = (parseFloat(a.payment) || 0) + (parseFloat(a.tips) || 0)
        bVal = (parseFloat(b.payment) || 0) + (parseFloat(b.tips) || 0)
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 :  1
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1

      // Same date (or same total) -- break the tie by time so gigs on
      // the same day still display earliest-to-latest regardless of
      // ascending/descending date sort direction.
      if (sortField === 'date') {
        const aTime = a.call_time || a.time || ''
        const bTime = b.call_time || b.time || ''
        const timeCmp = aTime.localeCompare(bTime)
        return sortDir === 'asc' ? timeCmp : -timeCmp
      }
      return 0
    })
  }, [gigs, filter, selectedYear, selectedMonth, selectedVenue, selectedCity, locationTab, homeLocation, searchTerm, today, sortField, sortDir])

  useEffect(() => {
    if (selectedYear !== 'all' && !availableYears.includes(parseInt(selectedYear))) {
      setParam('year', 'all')
      setParam('month', 'all')
    } else if (selectedMonth !== 'all' && !availableMonths.includes(parseInt(selectedMonth))) {
      setParam('month', 'all')
    }
  }, [filter, availableYears, availableMonths]) // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams()
      const tab = prev.get('tab') || 'upcoming'
      next.set('tab', tab)
      return next
    }, { replace: true })
  }

  const hasActiveFilters =
    locationTab   !== 'all' ||
    selectedYear  !== 'all' ||
    selectedMonth !== 'all' ||
    selectedVenue !== 'all' ||
    selectedCity  !== 'all' ||
    searchTerm    !== ''

  // Returns the badge config for Home/Away, or null if city is unknown.
  const homeBadge = (gig) => {
    if (!homeLocation) return null
    const gigCity = (gig.city || '').trim()
    if (!gigCity) return null
    const home = isHomeGig(gig, homeLocation)
    if (home === null) return null
    return home ? 'home' : 'away'
  }

  // ── Inline "Private gig" toggle (per gig card, saves immediately) ──
  const [savingPrivateId, setSavingPrivateId] = useState(null)
  const [privateAlertId,  setPrivateAlertId]  = useState(null)
  const privateAlertTimerRef = useRef(null)

  // ── Inline contract upload (per gig card) ────────────────────
  const [uploadingContractId, setUploadingContractId] = useState(null)

  const handleTogglePrivate = async (gigId, e) => {
    const checked = e.target.checked
    e.stopPropagation()
    setSavingPrivateId(gigId)
    try {
      await updateGig(gigId, { is_private: checked })
      setPrivateAlertId({ id: gigId, state: checked ? 'on' : 'off' })
      if (privateAlertTimerRef.current) clearTimeout(privateAlertTimerRef.current)
      privateAlertTimerRef.current = setTimeout(() => setPrivateAlertId(null), 2500)
    }
    catch (error) { alert('Error updating private status: ' + error.message) }
    finally { setSavingPrivateId(null) }
  }

  const handleContractFileChange = async (gigId, e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // reset so picking the same file twice still fires onChange
    if (!file) return
    if (!isAllowedContractFile(file)) {
      alert('Contracts must be a PDF, DOC, or DOCX file.')
      return
    }
    setUploadingContractId(gigId)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { alert('Not logged in.'); return }
      const url = await uploadContractFile(gigId, user.id, file)
      await updateGig(gigId, { contract_url: url })
    } catch (error) {
      alert('Error uploading contract: ' + error.message)
    } finally {
      setUploadingContractId(null)
    }
  }

  if (loading || homeLocation === undefined) {
    return (
      <div className="gig-list-page">
        <h1 className="page-title">Gigs</h1>
        <p>Loading your gigs...</p>
      </div>
    )
  }

  return (
    <div className="gig-list-page">

      <div className="page-header-row">
        <h1 className="page-title">Gigs</h1>
        <button className="btn-primary" onClick={() => navigate('/gigs/new')}>+ Add Gig</button>
      </div>

      {/* ── Status tabs (upcoming / past / all) ── */}
      <div className="filter-tabs" >
        {['upcoming', 'past', 'all'].map(tabName => (
          <button
            key={tabName}
            className={filter === tabName ? 'active' : ''}
            onClick={() => setParam('tab', tabName)}
          >
            {tabName.charAt(0).toUpperCase() + tabName.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Home / Away location tabs ── */}
      {homeLocation && (
        <div className="filter-tabs">
          {[
            { key: 'all',  label: 'All Locations',  icon: 'bi-geo' },
            { key: 'home', label: `Home (${homeLocation.city.replace(/\b\w/g, c => c.toUpperCase())})`, icon: 'bi-house-heart-fill' },
            { key: 'away', label: 'Away', icon: 'bi-airplane-fill' },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              className={locationTab === key ? 'active' : ''}
              onClick={() => setParam('loc', key)}
            >
              <i className={`bi ${icon} gig-filter-tab-icon`}></i>{label}
            </button>
          ))}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="earnings-filter-bar">

        <span className="earnings-filter-label">
          <i className="bi bi-funnel-fill"></i> Filter:
        </span>

        <div className="earnings-filter-group">
          <label className="earnings-filter-field-label">Year:</label>
          <select
            value={selectedYear}
            onChange={e => { setParam('year', e.target.value); setParam('month', 'all') }}
            className="earnings-select"
          >
            <option value="all">All Years</option>
            {availableYears.map(y => <option key={y} value={String(y)}>{y}</option>)}
          </select>
        </div>

        <div className="earnings-filter-group">
          <label className="earnings-filter-field-label">Month:</label>
          <select
            value={selectedMonth}
            onChange={e => setParam('month', e.target.value)}
            className="earnings-select"
          >
            <option value="all">All Months</option>
            {availableMonths.map(idx => (
              <option key={idx} value={String(idx)}>{monthNames[idx]}</option>
            ))}
          </select>
        </div>

        {availableVenues.length > 0 && (
          <div className="earnings-filter-group">
            <label className="earnings-filter-field-label">Venue:</label>
            <select
              value={selectedVenue}
              onChange={e => setParam('venue', e.target.value)}
              className="earnings-select"
            >
              <option value="all">All Venues</option>
              {availableVenues.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}

        {availableCities.length > 0 && (
          <div className="earnings-filter-group">
            <label className="earnings-filter-field-label">City:</label>
            <select
              value={selectedCity}
              onChange={e => setParam('city', e.target.value)}
              className="earnings-select"
            >
              <option value="all">All Cities</option>
              {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <div className="earnings-filter-group earnings-search-group">
          <label className="earnings-filter-field-label">Gig:</label>
          <div className="earnings-search-wrapper">
            <input
              type="text"
              placeholder="Search by gig name..."
              value={searchTerm}
              onChange={e => setParam('q', e.target.value)}
              className="earnings-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setParam('q', '')}
                className="earnings-search-clear"
                aria-label="Clear search"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <button onClick={clearAllFilters} className="earnings-clear-all-btn">
            <i className="bi bi-x-circle"></i> Clear Filters
          </button>
        )}

      </div>

      {/* ── Sort bar ── */}
      <div className="gig-sort-bar">
        <span className="gig-sort-label">
          <i className="bi bi-sort-down gig-sort-bar-icon"></i>Sort:
        </span>
        {[
          { field: 'date',  label: 'Date' },
          ...(!hideEarnings ? [{ field: 'total', label: 'Gig Total' }] : []),
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={`gig-sort-btn${sortField === field ? ' gig-sort-btn--active' : ''}`}
          >
            {label}{sortIcon(field)}
          </button>
        ))}
        <span className="gig-sort-count">
          {filteredGigs.length} gig{filteredGigs.length !== 1 ? 's' : ''}
          {hasActiveFilters && ' (filters active)'}
        </span>
      </div>

      {/* ── Gig list or empty state ── */}
      {filteredGigs.length === 0 ? (
        <div className="gig-list-empty">
          <p>{hasActiveFilters ? 'No gigs match these filters.' : `No ${filter !== 'all' ? filter : ''} gigs yet.`}</p>
          {hasActiveFilters
            ? <button className="btn-secondary" onClick={clearAllFilters}>Clear Filters</button>
            : <button className="btn-primary" onClick={() => navigate('/gigs/new')}>+ Add Your First Gig</button>
          }
        </div>
      ) : (
        <div className="gig-list">
          {filteredGigs.map(gig => {
            const badge = homeBadge(gig)
            return (
              <Link key={gig.id} to={`/gigs/${gig.id}`} className="gig-card-link">
                <div className="gig-card">
                  <div className="gig-card-header">
                    <div className="gig-card-info-row">

                      {/* Name + badges row */}
                      <div className="gig-card-name-row">
                        <h3 className="mb-0">{gig.name || gig.venue_name}</h3>

                        {badge && (
                          <span className={`gig-badge gig-badge--${badge}`}>
                            <i className={`bi ${badge === 'home' ? 'bi-house-heart-fill' : 'bi-airplane-fill'}`}></i>
                            {badge === 'home' ? 'Home' : 'Away'}
                          </span>
                        )}

                        {gig.is_managed && (
                          <span className="gig-badge gig-badge--managed">
                            <i className="bi bi-briefcase-fill"></i> Managed
                          </span>
                        )}

                        {/* Contract badge, managed gigs only. "View" opens the file in a
                            new tab. "Add" opens a hidden file input. stopPropagation
                            everywhere so the card's own Link doesn't also fire. */}
                        {gig.is_managed && (
                          gig.contract_url ? (
                            <a
                              href={gig.contract_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="gig-badge gig-badge--contract"
                            >
                              <i className="bi bi-file-earmark-text-fill"></i> Contract
                            </a>
                          ) : (
                            <label
                              className="gig-badge gig-badge--contract-add"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleContractFileChange(gig.id, e)}
                                className="gig-hidden-file-input"
                                disabled={uploadingContractId === gig.id}
                              />
                              <i className={`bi ${uploadingContractId === gig.id ? 'bi-arrow-repeat spin' : 'bi-file-earmark-plus'}`}></i>
                              {uploadingContractId === gig.id ? ' Uploading...' : ' Add Contract'}
                            </label>
                          )
                        )}

                        <label
                          className={`gig-badge gig-badge--private-toggle gig-private-toggle${gig.is_private ? ' gig-badge--private' : ''} ${savingPrivateId === gig.id ? 'gig-private-toggle--saving' : (gig.is_private ? 'gig-private-toggle--on' : 'gig-private-toggle--off')}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={!!gig.is_private}
                            onChange={(e) => handleTogglePrivate(gig.id, e)}
                            disabled={savingPrivateId === gig.id}
                            className="gig-check gig-check--xs"
                          />
                          <i className={`bi ${gig.is_private ? 'bi-lock-fill' : 'bi-unlock-fill'}`}></i> {gig.is_private ? 'Private' : 'Public'}
                        </label>

                        {/* "Request Pending" badge -- shown when a manager has sent a
                            management request for this specific gig and it hasn't been
                            acted on yet. Clicking the card still navigates to GigDetails. */}
                        {!gig.is_managed && pendingReqGigIds.has(gig.id) && (
                          <span className="gig-badge gig-badge--pending">
                            <i className="bi bi-bell-fill"></i> Request Pending
                          </span>
                        )}
                      </div>

                      <p className="gig-card-venue-name">{gig.venue_name}</p>

                      {(gig.city || gig.state) && (
                        <p className="gig-city-line">
                          {[gig.city, gig.state].filter(Boolean).join(', ')}
                        </p>
                      )}

                      {gig.venue_address && (
                        <p className="gig-card-address">
                          <a
                            href={mapsUrl(gig.venue_address)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="gig-map-link gig-map-link--sm"
                          >
                            <i className="bi bi-map-fill"></i> {gig.venue_address}
                          </a>
                        </p>
                      )}
                    </div>

                    {/* ── Payment column ── */}
                    {!hideEarnings && (
                      <div className="gig-card-payment-col">
                        {(gig.is_managed && (gig.manager_fee_pct != null || gig.manager_fee_flat != null)) ? (
                          <>
                            <div className="gig-card-payment-strikethrough">
                              ${parseFloat(gig.payment || 0).toFixed(2)}
                            </div>
                            <div className="gig-card-payment-keep">
                              ${(parseFloat(gig.payment || 0) - calcFee(gig.payment, gig.manager_fee_type, gig.manager_fee_pct, gig.manager_fee_flat)).toFixed(2)}
                              <span className="gig-card-payment-keep-label">keep</span>
                            </div>
                          </>
                        ) : (
                          <div className="gig-card-payment-self">
                            ${gig.payment}
                          </div>
                        )}
                      </div>
                    )}

                  </div>

                  <div className="gig-card-meta">
                    <span><i className="bi bi-calendar3"></i> {formatDate(gig.date)}</span>
                    <span><i className="bi bi-clock"></i> {fmtShowTime(gig.time)}</span>
                    {gig.setlist && gig.setlist.length > 0 && (
                      <span className="gig-card-setlist-count">
                        <i className="bi bi-music-note-list"></i> {gig.setlist.length} song{gig.setlist.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {privateAlertId?.id === gig.id && (
                    <div className="gig-private-alert gig-private-alert--sm">
                      <i className="bi bi-check-circle-fill gig-icon-tertiary2"></i>
                      {privateAlertId.state === 'on' ? 'This gig is now private.' : 'This gig is now public.'}
                    </div>
                  )}

                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}


// ============================================================
// GigDetails
// ============================================================
export function GigDetails() {

  const { id }                         = useParams()
  const navigate                       = useNavigate()
  const { gigs, loading, deleteGig, updateGig } = useGigs()
  const { tracks }                     = useMusicTracks()

  const {
    currentTrack: globalCurrentTrack,
    isPlaying:    globalIsPlaying,
    playTrack:    playGlobalTrack,
    togglePlay:   globalTogglePlay,
    stop:         stopGlobal,
    setQueue,
    repeatMode:   globalRepeatMode,
    cycleRepeat:  globalCycleRepeat,
  } = useMusicPlayer()

  const repeatMode  = globalRepeatMode                               || 'none'
  const cycleRepeat = typeof globalCycleRepeat === 'function' ? globalCycleRepeat : () => {}

  const [tips,          setTips]          = useState('')
  const [editingTips,   setEditingTips]   = useState(false)
  const [editTipsValue, setEditTipsValue] = useState('')
  const [savingTips,    setSavingTips]    = useState(false)
  const [showAddSong,   setShowAddSong]   = useState(false)
  const [songSearch,    setSongSearch]    = useState('')

  const [showSendMusicModal, setShowSendMusicModal] = useState(false)
  const [sendMusicInfo, setSendMusicInfo]           = useState({ performerName: '', showName: '' })
  const [showCalendarMenu, setShowCalendarMenu]     = useState(false)
  const calendarMenuRef = useRef(null)

  const [managerName, setManagerName] = useState(null)

  // ── Inline "Notes" edit (Gig Details page) ──
  const [editingNotes,   setEditingNotes]   = useState(false)
  const [editNotesValue, setEditNotesValue] = useState('')
  const [savingNotes,    setSavingNotes]    = useState(false)

  // ── Inline "Getting Ready & Travel" edit (Gig Details page) ──
  const [editingReady,    setEditingReady]    = useState(false)
  const [editReadyStep1,  setEditReadyStep1]  = useState('home')
  const [editReadySplit,  setEditReadySplit]  = useState('full')
  const [editPrepAddress, setEditPrepAddress] = useState('')
  const [editPrepLat,     setEditPrepLat]     = useState(null)
  const [editPrepLng,     setEditPrepLng]     = useState(null)
  const [savingReady,     setSavingReady]     = useState(false)
  const [readyPrepSuggestions,     setReadyPrepSuggestions]     = useState([])
  const [showReadyPrepSuggestions, setShowReadyPrepSuggestions] = useState(false)
  const [readyPrepSearching,       setReadyPrepSearching]       = useState(false)
  const readyPrepDebounceRef = useRef(null)

  // ── Inline "Private gig" toggle (Gig Details page) ──
  const [savingPrivate, setSavingPrivate] = useState(false)

  // ── Contract: inline upload and view (managed gigs) ───────────
  const [uploadingContract, setUploadingContract] = useState(false)
  const contractInputRef = useRef(null)

  useEffect(() => {
    const managerId = gigs.find(g => g.id === id)?.manager_id
    if (!managerId) { setManagerName(null); return }
    supabase
      .from('manager_profiles')
      .select('display_name')
      .eq('id', managerId)
      .maybeSingle()
      .then(({ data }) => setManagerName(data?.display_name || null))
  }, [gigs, id])

  useEffect(() => {
    const handler = (e) => {
      if (calendarMenuRef.current && !calendarMenuRef.current.contains(e.target))
        setShowCalendarMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      setSendMusicInfo(prev => ({ ...prev, performerName: user.user_metadata?.performer_name || '' }))
    })
  }, [])

  useEffect(() => {
    if (gigs.length > 0) {
      const currentGig = gigs.find(g => g.id === id)
      if (currentGig)
        setSendMusicInfo(prev => ({ ...prev, showName: currentGig.name || currentGig.venue_name || '' }))
    }
  }, [id, gigs])

  const gig             = gigs.find(g => g.id === id)
  const setlistTrackIds = gig?.setlist || []
  const setlistTracks   = setlistTrackIds.map(tid => tracks.find(t => t.id === tid)).filter(Boolean)

  const setlistCurrentIndex = globalCurrentTrack
    ? setlistTracks.findIndex(t => t.url === globalCurrentTrack.url)
    : -1
  const setlistPlaying = setlistCurrentIndex >= 0 && globalIsPlaying

  const filteredLibraryTracks = tracks
    .filter(t => !setlistTrackIds.includes(t.id))
    .filter(t => t.name.toLowerCase().includes(songSearch.toLowerCase()))

  const parseGigDate = (dateStr) => {
    const parts = dateStr.split('T')[0].split('-')
    return new Date(parts[0], parts[1] - 1, parts[2])
  }

  const formatDate = (dateStr) =>
    parseGigDate(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })

  const downloadICS = () => {
    if (!gig) return
    const { title, location, descLines, dtStart, dtEnd, tzid } = buildAudienceEventData(gig)
    const icsContent = [
      'BEGIN:VCALENDAR', 'VERSION:2.0',
      'PRODID:-//Drag Pocket Planner//Show Calendar//EN',
      'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
      `UID:${gig.id}-audience@dragpocketplanner`,
      `SUMMARY:${icsEscape(title)}`,
      `DTSTART;TZID=${tzid}:${dtStart}`,
      `DTEND;TZID=${tzid}:${dtEnd}`,
      location             ? `LOCATION:${icsEscape(location)}`      : '',
      descLines.length > 0 ? `DESCRIPTION:${descLines.map(icsEscape).join('\\n')}` : '',
      'STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR',
    ].filter(Boolean).join('\r\n')
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url; link.download = `${title.replace(/\s+/g, '_')}.ics`
    document.body.appendChild(link); link.click()
    document.body.removeChild(link); URL.revokeObjectURL(url)
    setShowCalendarMenu(false)
  }

  const openGoogleCalendar = () => {
    if (!gig) return
    const { title, location, descLines, dtStart, dtEnd } = buildAudienceEventData(gig)
    const params = new URLSearchParams({ action: 'TEMPLATE', text: title, dates: `${dtStart}/${dtEnd}`, details: descLines.join('\n'), location })
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer')
    setShowCalendarMenu(false)
  }

  const handleSendMusic = () => {
    const count     = setlistTracks.length
    const songWord  = count === 1 ? 'song' : 'songs'
    const perfLabel = sendMusicInfo.performerName
    const showLabel = sendMusicInfo.showName
    const trackLines = setlistTracks.map((t, index) => {
      const cleanPerformer = perfLabel.trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')
      const cleanTrack     = t.name.trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')
      const ext            = (t.url || '').split('?')[0].split('.').pop() || 'mp3'
      const displayName    = `${cleanPerformer}_${index + 1}_${cleanTrack}.${ext}`
      const baseUrl        = (t.url || '').split('?')[0]
      const downloadUrl    = `${baseUrl}?download=${encodeURIComponent(displayName)}`
      return `• ${displayName}\n  ${downloadUrl}`
    }).join('\n\n')
    const bodyText = `Hi!\n\n${perfLabel} here. Here ${count === 1 ? 'is my' : 'are my'} ${songWord} for ${showLabel}. Just click the link to instantly download the tracks!:\n\n${trackLines}\n\nThank you so much, let me know if you have any issues.\n${perfLabel}`
    const subject  = encodeURIComponent(`${perfLabel}'s ${songWord} for ${showLabel}: ${count} track${count !== 1 ? 's' : ''}`)
    const bodyEnc  = encodeURIComponent(bodyText)
    const toField  = gig.contact_email ? `${encodeURIComponent(gig.contact_email)}?` : '?'
    window.location.href = `mailto:${toField}subject=${subject}&body=${bodyEnc}`
    setShowSendMusicModal(false)
  }

  const handleMarkComplete = async () => {
    try { await updateGig(id, { completed: true, tips: parseFloat(tips) || 0 }); alert('Gig marked as complete!') }
    catch (error) { alert('Error completing gig: ' + error.message) }
  }

  const handleSaveTips = async () => {
    setSavingTips(true)
    try {
      const newTips = parseFloat(editTipsValue)
      if (isNaN(newTips) || newTips < 0) { alert('Please enter a valid tip amount (0 or more).'); setSavingTips(false); return }
      await updateGig(id, { tips: newTips }); setEditingTips(false); setEditTipsValue('')
    } catch (error) { alert('Error updating tips: ' + error.message) }
    finally { setSavingTips(false) }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this gig?')) {
      try { await deleteGig(id); navigate('/gigs') }
      catch (error) { alert('Error deleting gig: ' + error.message) }
    }
  }

  const addToSetlist = async (trackId) => {
    if (setlistTrackIds.includes(trackId)) return
    try { await updateGig(id, { setlist: [...setlistTrackIds, trackId] }) }
    catch (error) { alert('Error saving setlist: ' + error.message) }
  }

  const removeFromSetlist = async (trackId) => {
    const removedTrack = setlistTracks.find(t => t.id === trackId)
    if (removedTrack && globalCurrentTrack?.url === removedTrack.url && typeof stopGlobal === 'function')
      stopGlobal()
    try { await updateGig(id, { setlist: setlistTrackIds.filter(tid => tid !== trackId) }) }
    catch (error) { alert('Error saving setlist: ' + error.message) }
  }

  const moveTrack = async (index, direction) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= setlistTrackIds.length) return
    const newSetlist = [...setlistTrackIds]
    ;[newSetlist[index], newSetlist[targetIndex]] = [newSetlist[targetIndex], newSetlist[index]]
    try { await updateGig(id, { setlist: newSetlist }) }
    catch (error) { alert('Error saving setlist: ' + error.message) }
  }

  const playTrack = (index) => {
    const mappedQueue = setlistTracks.map(t => ({ name: t.name, url: t.url }))
    if (typeof setQueue === 'function') setQueue(mappedQueue, index)
    else { const track = setlistTracks[index]; if (track) playGlobalTrack({ name: track.name, url: track.url }) }
  }

  const pauseTrack = () => { if (globalIsPlaying) globalTogglePlay() }

  // ── Notes: inline edit ──
  const startEditingNotes = () => {
    setEditNotesValue(gig.notes || '')
    setEditingNotes(true)
  }

  const handleSaveNotes = async () => {
    setSavingNotes(true)
    try {
      await updateGig(id, { notes: editNotesValue })
      setEditingNotes(false)
    } catch (error) { alert('Error updating notes: ' + error.message) }
    finally { setSavingNotes(false) }
  }

  // ── Getting Ready & Travel: inline edit ──
  const startEditingReady = () => {
    const { step1, split } = parseCompoundReadyLocation(gig.get_ready_location)
    setEditReadyStep1(step1)
    setEditReadySplit(split)
    setEditPrepAddress(gig.prep_address || '')
    setEditPrepLat(gig.prep_lat ?? null)
    setEditPrepLng(gig.prep_lng ?? null)
    setReadyPrepSuggestions([])
    setShowReadyPrepSuggestions(false)
    setEditingReady(true)
  }

  const handleReadyPrepAddressChange = (e) => {
    const value = e.target.value
    setEditPrepAddress(value)
    setEditPrepLat(null)
    setEditPrepLng(null)
    if (readyPrepDebounceRef.current) clearTimeout(readyPrepDebounceRef.current)
    if (value.trim().length < 3) { setReadyPrepSuggestions([]); setShowReadyPrepSuggestions(false); return }
    readyPrepDebounceRef.current = setTimeout(async () => {
      setReadyPrepSearching(true)
      try {
        const params  = new URLSearchParams({ q: value, format: 'json', limit: '5', addressdetails: '1' })
        const res     = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'User-Agent': NOMINATIM_USER_AGENT } })
        if (!res.ok) throw new Error()
        const results = await res.json()
        setReadyPrepSuggestions(results)
        setShowReadyPrepSuggestions(results.length > 0)
      } catch { setReadyPrepSuggestions([]); setShowReadyPrepSuggestions(false) }
      finally { setReadyPrepSearching(false) }
    }, 400)
  }

  const handleReadyPrepSelect = (result) => {
    const addr     = result.address || {}
    const namePart = result.name || result.display_name.split(',')[0].trim()
    const street   = addr.house_number && addr.road ? `${addr.house_number} ${addr.road}` : addr.road || ''
    const city     = addr.city || addr.town || addr.village || ''
    const parts    = [namePart, street, city, addr.state || ''].filter((p, i) => p && (i < 2 || p !== namePart))
    const label    = parts.slice(0, 3).join(', ') || result.display_name.split(',').slice(0, 3).join(',').trim()
    setEditPrepAddress(label)
    setEditPrepLat(parseFloat(result.lat))
    setEditPrepLng(parseFloat(result.lon))
    setReadyPrepSuggestions([])
    setShowReadyPrepSuggestions(false)
  }

  const handleSaveReady = async () => {
    setSavingReady(true)
    try {
      const get_ready_location = buildCompoundReadyLocation(editReadyStep1, editReadySplit)
      const prepAddress = editReadyStep1 === 'other' ? editPrepAddress : null
      const prepLat     = editReadyStep1 === 'other' ? editPrepLat     : null
      const prepLng     = editReadyStep1 === 'other' ? editPrepLng     : null
      await updateGig(id, {
        get_ready_location,
        prep_address: prepAddress,
        prep_lat:     prepLat,
        prep_lng:     prepLng,
      })
      setEditingReady(false)
    } catch (error) { alert('Error updating getting ready info: ' + error.message) }
    finally { setSavingReady(false) }
  }

  // ── Private gig: inline toggle, saves immediately ──
  const [privateAlert, setPrivateAlert] = useState(null)
  const privateAlertTimerRef = useRef(null)

  const handleTogglePrivate = async (e) => {
    const checked = e.target.checked
    setSavingPrivate(true)
    try {
      await updateGig(id, { is_private: checked })
      setPrivateAlert(checked ? 'on' : 'off')
      if (privateAlertTimerRef.current) clearTimeout(privateAlertTimerRef.current)
      privateAlertTimerRef.current = setTimeout(() => setPrivateAlert(null), 2500)
    }
    catch (error) { alert('Error updating private status: ' + error.message) }
    finally { setSavingPrivate(false) }
  }

  const handleContractFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!isAllowedContractFile(file)) {
      alert('Contracts must be a PDF, DOC, or DOCX file.')
      return
    }
    setUploadingContract(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { alert('Not logged in.'); return }
      const url = await uploadContractFile(id, user.id, file)
      await updateGig(id, { contract_url: url })
    } catch (error) {
      alert('Error uploading contract: ' + error.message)
    } finally {
      setUploadingContract(false)
    }
  }

  if (loading) return <div className="gig-details-page"><p>Loading gig details...</p></div>
  if (!gig) return (
    <div className="gig-details-page">
      <h1>Gig not found</h1>
      <Link to="/gigs" className="back-link">← Back to Gigs</Link>
    </div>
  )


  return (
    <div className="gig-details-page">
      <Link to="/gigs" className="back-link">← Back to Gigs</Link>

      <div className="gig-detail-card">
        <div className="gig-flex-between">
          <h1 className="gig-m0">{gig.name || gig.venue_name}</h1>
          <label
            className={`gig-badge gig-badge--private-toggle gig-private-toggle${gig.is_private ? ' gig-badge--private' : ''} ${savingPrivate ? 'gig-private-toggle--saving' : (gig.is_private ? 'gig-private-toggle--on' : 'gig-private-toggle--off')}`}
          >
            <input
              type="checkbox"
              checked={!!gig.is_private}
              onChange={handleTogglePrivate}
              disabled={savingPrivate}
              className="gig-check gig-check--sm"
            />
            <i className={`bi ${gig.is_private ? 'bi-lock-fill' : 'bi-unlock-fill'}`}></i> {gig.is_private ? 'Private' : 'Public'}
          </label>
        </div>

        {privateAlert && (
          <div className="gig-private-alert">
            <i className="bi bi-check-circle-fill gig-icon-tertiary2"></i>
            {privateAlert === 'on' ? 'This gig is now private.' : 'This gig is now public.'}
          </div>
        )}
        <p>{gig.venue_name}</p>
        {gig.venue_address && (
          <p>
            <a href={mapsUrl(gig.venue_address)} target="_blank" rel="noopener noreferrer" className="gig-map-link">
              <i className="bi bi-map-fill"></i> {gig.venue_address}
            </a>
          </p>
        )}

        <div className="detail-grid">
          {/* Payment -- shows fee breakdown when managed */}
          <div className="detail-item">
            <span className="detail-label">Payment</span>
            <span className="detail-value">
              {gig.is_managed && (gig.manager_fee_pct != null || gig.manager_fee_flat != null) ? (
                <span>
                  <span className="gig-detail-payment-strikethrough">
                    ${parseFloat(gig.payment || 0).toFixed(2)}
                  </span>
                  <strong className="gig-detail-payment-cut">
                    ${(parseFloat(gig.payment || 0) - calcFee(gig.payment, gig.manager_fee_type, gig.manager_fee_pct, gig.manager_fee_flat)).toFixed(2)}
                  </strong>
                  <span className="gig-detail-payment-cut-label">your cut</span>
                </span>
              ) : (
                `$${gig.payment}`
              )}
            </span>
          </div>

          <div className="detail-item">
            <span className="detail-label">Date</span>
            <span className="detail-value">{formatDate(gig.date)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Time</span>
            <span className="detail-value">{fmtShowTime(gig.time)}</span>
          </div>
          {gig.timezone && (
            <div className="detail-item">
              <span className="detail-label">Timezone</span>
              <span className="detail-value">{TIMEZONES.find(tz => tz.value === gig.timezone)?.label || 'Eastern'}</span>
            </div>
          )}
          {gig.ticket_link && (
            <div className="detail-item">
              <span className="detail-label">Ticket / RSVP</span>
              <span className="detail-value">
                <a href={safeUrl(gig.ticket_link)} target="_blank" rel="noopener noreferrer">{gig.ticket_link}</a>
              </span>
            </div>
          )}
        </div>

        {/* ── Managed gig info panel ── */}
        {gig.is_managed && (
          <div className="gig-managed-panel">
            <div className="gig-managed-panel__header">
              <i className="bi bi-briefcase-fill gig-icon-primary-lg"></i>
              <span className="gig-managed-panel__title">Managed Gig</span>
              {managerName && (
                <span className="gig-managed-panel__manager">· {managerName}</span>
              )}
            </div>
            {(() => {
              const booking  = parseFloat(gig.payment || 0)
              const feeAmt   = calcFee(gig.payment, gig.manager_fee_type, gig.manager_fee_pct, gig.manager_fee_flat)
              const takeHome = booking - feeAmt
              const hasRate  = gig.manager_fee_pct != null || gig.manager_fee_flat != null
              if (!hasRate) return (
                <p className="gig-managed-panel__no-rate">
                  Your manager is tracking this gig. Fee details not yet set.
                </p>
              )
              return (
                <div className="gig-fee-breakdown">
                  <div className="gig-fee-row">
                    <span className="gig-fee-label">Booking fee</span>
                    <span className="gig-fee-amount">${booking.toFixed(2)}</span>
                  </div>
                  <div className="gig-fee-row">
                    <span className="gig-fee-label">
                      Manager fee ({feeLabel(gig.manager_fee_type, gig.manager_fee_pct, gig.manager_fee_flat)})
                    </span>
                    <span className="gig-fee-amount--cut">&minus;${feeAmt.toFixed(2)}</span>
                  </div>
                  <div className="gig-fee-total-row">
                    <span className="gig-fee-total-label">
                      <i className="bi bi-cash-stack dash-stat-transport-icon"></i>You keep
                    </span>
                    <span className="gig-fee-total-amount">${takeHome.toFixed(2)}</span>
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* ── Contract panel ── */}
        {gig.is_managed && (
          <div className="gig-contract-panel">
            <div className="gig-contract-panel__header">
              <i className="bi bi-file-earmark-text-fill dash-section-icon"></i>
              <span className="gig-contract-panel__title">Contract</span>
            </div>

            {gig.contract_url ? (
              <div className="gig-contract-panel__row">
                <a
                  href={gig.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  <i className="bi bi-eye-fill"></i> View Contract
                </a>
                <button
                  className="btn-secondary"
                  onClick={() => contractInputRef.current?.click()}
                  disabled={uploadingContract}
                >
                  <i className={`bi ${uploadingContract ? 'bi-arrow-repeat spin' : 'bi-arrow-repeat'}`}></i>
                  {uploadingContract ? ' Uploading...' : ' Replace'}
                </button>
              </div>
            ) : (
              <div className="gig-contract-panel__row">
                <p className="gig-contract-panel__empty">No contract uploaded yet.</p>
                <button
                  className="btn-primary"
                  onClick={() => contractInputRef.current?.click()}
                  disabled={uploadingContract}
                >
                  <i className={`bi ${uploadingContract ? 'bi-arrow-repeat spin' : 'bi-file-earmark-plus'}`}></i>
                  {uploadingContract ? ' Uploading...' : ' Upload Contract'}
                </button>
              </div>
            )}

            <input
              type="file"
              accept=".pdf,.doc,.docx"
              ref={contractInputRef}
              onChange={handleContractFileChange}
              className="gig-hidden-file-input"
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* ── Ready location panel ── */}
        <div className="gig-ready-panel">
          <div className="gig-ready-panel__header gig-flex-between">
            {!editingReady && (
              <p className="gig-m0 gig-ready-panel__label">
                <i className="bi bi-stars dash-section-icon"></i>
                Getting Ready: {getReadyLabel(gig.get_ready_location || 'home')}
              </p>
            )}
            {editingReady && (
              <p className="gig-m0 gig-ready-panel__label">
                <i className="bi bi-stars dash-section-icon"></i>
                Getting Ready &amp; Travel
              </p>
            )}
            {!editingReady && (
              <button className="btn-secondary" onClick={startEditingReady}>
                <i className="bi bi-pencil-fill"></i> Edit
              </button>
            )}
          </div>

          {!editingReady && gig.prep_address && (
            <p className="gig-ready-panel__address">
              <i className="bi bi-house-heart-fill dash-section-icon"></i>
              {gig.prep_address}
            </p>
          )}

          {editingReady && (
            <div className="gig-ready-panel__edit">
              <div className="form-group">
                <label className="mb-2 d-block">Step 1: Where are you starting from?</label>

                <label className={`radio-card${editReadyStep1 === 'home' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                  <input
                    type="radio"
                    name="detail-get-ready-location"
                    value="home"
                    checked={editReadyStep1 === 'home'}
                    onChange={() => setEditReadyStep1('home')}
                    className="gig-radio-input"
                  />
                  <div>
                    <div className="radio-card__title"><i className="bi bi-house-fill dash-section-icon"></i>Home</div>
                    <div className="radio-card__desc">Starting from your 'home address' or your 'trip home address'.</div>
                  </div>
                </label>

                <label className={`radio-card${editReadyStep1 === 'other' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                  <input
                    type="radio"
                    name="detail-get-ready-location"
                    value="other"
                    checked={editReadyStep1 === 'other'}
                    onChange={() => setEditReadyStep1('other')}
                    className="gig-radio-input"
                  />
                  <div>
                    <div className="radio-card__title"><i className="bi bi-house-heart-fill dash-section-icon"></i>Another location</div>
                    <div className="radio-card__desc">Hotel, friend's place, studio, or anywhere that isn't your usual home address.</div>
                  </div>
                </label>

                <label className={`radio-card radio-card--last${editReadyStep1 === 'venue' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                  <input
                    type="radio"
                    name="detail-get-ready-location"
                    value="venue"
                    checked={editReadyStep1 === 'venue'}
                    onChange={() => setEditReadyStep1('venue')}
                    className="gig-radio-input"
                  />
                  <div>
                    <div className="radio-card__title"><i className="bi bi-geo-alt-fill dash-section-icon"></i>At the venue</div>
                    <div className="radio-card__desc">Driving to the venue first, then getting ready on arrival.</div>
                  </div>
                </label>
              </div>

              {editReadyStep1 === 'other' && (
                <div className="form-group">
                  <label>
                    <i className="bi bi-house-heart-fill dash-section-icon"></i>
                    Address of where you are getting-ready *
                  </label>
                  <div className="venue-search-wrapper">
                    <input
                      type="text"
                      placeholder="Hotel name, friend's address, studio..."
                      value={editPrepAddress}
                      onChange={handleReadyPrepAddressChange}
                      onBlur={() => { setTimeout(() => setShowReadyPrepSuggestions(false), 150) }}
                      onFocus={() => { if (readyPrepSuggestions.length > 0) setShowReadyPrepSuggestions(true) }}
                      autoComplete="off"
                    />
                    {readyPrepSearching && (
                      <span className="input-search-spinner">
                        <i className="bi bi-arrow-repeat spin"></i>
                      </span>
                    )}
                    {showReadyPrepSuggestions && readyPrepSuggestions.length > 0 && (
                      <ul className="nominatim-dropdown">
                        {readyPrepSuggestions.map((result, i) => (
                          <li
                            key={result.place_id || i}
                            onMouseDown={() => handleReadyPrepSelect(result)}
                            className="nominatim-dropdown-item"
                          >
                            <div className="nominatim-item-name">{result.name || result.display_name.split(',')[0].trim()}</div>
                            <div className="nominatim-item-full">{result.display_name}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {editPrepLat && editPrepLng && (
                    <div className="geocoded-badge">
                      <i className="bi bi-check-circle-fill"></i>Location found! The dashboard will route from here.
                    </div>
                  )}
                  <small>Start typing and select from the list.</small>
                </div>
              )}

              {editReadyStep1 !== 'venue' && (
                <div className="form-group">
                  <label className="mb-2 d-block">
                    Step 2: How much prep happens {editReadyStep1 === 'other' ? 'at that location' : 'at home'} before you leave?
                  </label>

                  <label className={`radio-card${editReadySplit === 'full' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                    <input
                      type="radio"
                      name="detail-prep-split"
                      value="full"
                      checked={editReadySplit === 'full'}
                      onChange={() => setEditReadySplit('full')}
                      className="gig-radio-input"
                    />
                    <div>
                      <div className="radio-card__title"><i className="bi bi-stars dash-section-icon"></i>Fully ready before leaving</div>
                      <div className="radio-card__desc">
                        {editReadyStep1 === 'other'
                          ? 'All hair, makeup, and costume done at this location.'
                          : 'All hair, makeup, and costume done at home.'}
                      </div>
                    </div>
                  </label>

                  <label className={`radio-card${editReadySplit === 'half' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                    <input
                      type="radio"
                      name="detail-prep-split"
                      value="half"
                      checked={editReadySplit === 'half'}
                      onChange={() => setEditReadySplit('half')}
                      className="gig-radio-input"
                    />
                    <div>
                      <div className="radio-card__title"><i className="bi bi-slash-circle dash-section-icon"></i>Half and half</div>
                      <div className="radio-card__desc">50% prep before leaving, finish the other 50% at the venue.</div>
                    </div>
                  </label>

                  <label className={`radio-card radio-card--last${editReadySplit === 'most' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
                    <input
                      type="radio"
                      name="detail-prep-split"
                      value="most"
                      checked={editReadySplit === 'most'}
                      onChange={() => setEditReadySplit('most')}
                      className="gig-radio-input"
                    />
                    <div>
                      <div className="radio-card__title"><i className="bi bi-percent dash-section-icon"></i>Mostly ready, finishing at venue</div>
                      <div className="radio-card__desc">70% done before leaving. Last 30% at the venue: body, nails, contacts, lashes.</div>
                    </div>
                  </label>
                </div>
              )}

              <div className="gig-tips-row">
                <button className="btn-primary" onClick={handleSaveReady} disabled={savingReady}>
                  {savingReady ? 'Saving...' : 'Save'}
                </button>
                <button className="btn-secondary" onClick={() => setEditingReady(false)} disabled={savingReady}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Errand panel ── */}
        {gig.errand_address && (
          <div className="gig-errand-panel">
            <p>
              <i className="bi bi-bag-fill dash-section-icon"></i>
              <strong>Errand:</strong> {gig.errand_address}
              {gig.errand_duration && (
                <span className="gig-errand-duration">({gig.errand_duration} min)</span>
              )}
            </p>
          </div>
        )}

        {/* ── Contact info ── */}
        {gig.contact_name && (
          <div>
            <h3>Contact</h3>
            <p>{gig.contact_name}</p>
            {gig.contact_email    && <p>Email: <a href={`mailto:${gig.contact_email}`}>{gig.contact_email}</a></p>}
            {gig.contact_phone    && <p>Phone: <a href={`tel:${gig.contact_phone}`}>{gig.contact_phone}</a></p>}
            {gig.contact_instagram && (
              <p>Instagram: <a href={`https://instagram.com/${gig.contact_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">{gig.contact_instagram}</a></p>
            )}
          </div>
        )}

        {/* ── Notes ── */}
        <div>
          <div className="gig-flex-between">
            <h3 className="mb-0">Notes</h3>
            {!editingNotes && (
              <button className="btn-secondary" onClick={startEditingNotes}>
                <i className="bi bi-pencil-fill"></i> Edit
              </button>
            )}
          </div>

          {!editingNotes && (
            <p className="gig-notes">{gig.notes || 'No notes yet.'}</p>
          )}

          {editingNotes && (
            <div>
              <textarea
                className="gig-notes-textarea"
                value={editNotesValue}
                onChange={(e) => setEditNotesValue(e.target.value)}
                rows="8"
                placeholder="Set list, outfit, equipment needed..."
                autoFocus
              />
              <div className="gig-tips-row">
                <button className="btn-primary" onClick={handleSaveNotes} disabled={savingNotes}>
                  {savingNotes ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => { setEditingNotes(false); setEditNotesValue('') }}
                  disabled={savingNotes}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Tips entry (incomplete gig) ── */}
        {!gig.completed && (
          <div className="gig-tips-entry">
            <h3>Tips Earned</h3>
            <p className="text-muted">Enter tips after completing this gig:</p>
            <input
              type="number"
              placeholder="0.00"
              value={tips}
              onChange={(e) => setTips(e.target.value)}
              className="tips-edit-input"
            />
          </div>
        )}

        {/* ── Completed panel ── */}
        {gig.completed && (
          <div className="gig-completed-panel">
            <div className="gig-completed-header">
              <span className="gig-completed-label">
                <i className="bi bi-check-circle-fill"></i> Completed
              </span>
              {!editingTips && (
                <button
                  className="btn-secondary"
                  onClick={() => { setEditTipsValue(gig.tips != null ? String(gig.tips) : '0'); setEditingTips(true) }}
                >
                  <i className="bi bi-pencil-fill"></i> Edit Tips
                </button>
              )}
            </div>

            {!editingTips && (
              <p className="gig-tips-text">
                Tips:{' '}
                <strong className={gig.tips > 0 ? 'text-success' : ''}>
                  {gig.tips > 0 ? `$${Number(gig.tips).toFixed(2)}` : 'None recorded'}
                </strong>
              </p>
            )}

            {editingTips && (
              <div className="gig-tips-row">
                <label className="gig-tips-label">Tips ($):</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={editTipsValue}
                  onChange={(e) => setEditTipsValue(e.target.value)}
                  className="tips-edit-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')  handleSaveTips()
                    if (e.key === 'Escape') { setEditingTips(false); setEditTipsValue('') }
                  }}
                />
                <button className="btn-primary" onClick={handleSaveTips} disabled={savingTips}>
                  {savingTips ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => { setEditingTips(false); setEditTipsValue('') }}
                  disabled={savingTips}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── SETLIST ── */}
        <div className="gig-setlist-section">
          <div className="gig-setlist-header">
            <h3 className="mb-0">
              <i className="bi bi-music-note-list dash-section-icon"></i>
              Setlist
              {setlistTracks.length > 0 && (
                <span className="gig-setlist-count">
                  ({setlistTracks.length} song{setlistTracks.length !== 1 ? 's' : ''})
                </span>
              )}
            </h3>
            <button
              className="btn-secondary"
              onClick={() => { setShowAddSong(!showAddSong); setSongSearch('') }}
            >
              <i className={`bi ${showAddSong ? 'bi-x' : 'bi-plus-lg'}`}></i>
              {showAddSong ? ' Close' : ' Add Song'}
            </button>
          </div>

          {/* ── Add song panel ── */}
          {showAddSong && (
            <div className="gig-setlist-add-panel">
              <p className="gig-setlist-add-title">Pick from your music library:</p>
              {tracks.length > 4 && (
                <input
                  type="text"
                  placeholder="Search songs..."
                  value={songSearch}
                  onChange={(e) => setSongSearch(e.target.value)}
                  className="setlist-library-search"
                />
              )}
              {tracks.length === 0 ? (
                <p className="field-hint">
                  You have no songs in your music library yet. <Link to="/music">Upload some tracks →</Link>
                </p>
              ) : filteredLibraryTracks.length === 0 ? (
                <p className="field-hint">
                  {songSearch ? 'No tracks match your search.' : 'All your tracks are already in this setlist!'}
                </p>
              ) : (
                <div className="setlist-library-items">
                  {filteredLibraryTracks.map(track => (
                    <div key={track.id} className="setlist-library-item">
                      <div>
                        <span className="setlist-library-item-name">{track.name}</span>
                        {track.tags && track.tags.length > 0 && (
                          <span className="setlist-library-item-tags">#{track.tags.join(' #')}</span>
                        )}
                      </div>
                      <button className="btn-primary" onClick={() => addToSetlist(track.id)}>
                        <i className="bi bi-pin-fill"></i> Pin
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Empty setlist ── */}
          {setlistTracks.length === 0 ? (
            <div className="gig-setlist-empty">
              <i className="bi bi-music-note-beamed gig-setlist-empty-icon"></i>
              <p className="gig-setlist-empty-text">No songs pinned yet.</p>
              <p className="gig-setlist-empty-hint">Click "Add Song" to build your setlist.</p>
            </div>
          ) : (
            <div>
              {setlistTracks.map((track, index) => {
                const isThisTrackPlaying = globalCurrentTrack?.url === track.url && globalIsPlaying
                return (
                  <div
                    key={track.id}
                    className={`setlist-track-row${isThisTrackPlaying ? ' setlist-track-row--playing' : ''}`}
                  >
                    <span className="setlist-track-num">{index + 1}</span>
                    <button
                      onClick={() => isThisTrackPlaying ? pauseTrack() : playTrack(index)}
                      className="setlist-track-play-btn"
                    >
                      <i className={`bi ${isThisTrackPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                    </button>
                    <div className="setlist-track-info">
                      <div className="setlist-track-name">{track.name}</div>
                      {track.tags && track.tags.length > 0 && (
                        <div className="setlist-track-tags">#{track.tags.join(' #')}</div>
                      )}
                    </div>
                    <div className="setlist-track-controls">
                      <button
                        onClick={() => moveTrack(index, -1)}
                        disabled={index === 0}
                        title="Move up"
                        className="setlist-track-ctrl-btn"
                      >
                        <i className="bi bi-chevron-up"></i>
                      </button>
                      <button
                        onClick={() => moveTrack(index, 1)}
                        disabled={index === setlistTracks.length - 1}
                        title="Move down"
                        className="setlist-track-ctrl-btn"
                      >
                        <i className="bi bi-chevron-down"></i>
                      </button>
                      <button
                        onClick={() => removeFromSetlist(track.id)}
                        title="Remove from setlist"
                        className="setlist-track-ctrl-btn setlist-track-ctrl-btn--remove"
                      >
                        <i className="bi bi-x-circle-fill"></i>
                      </button>
                    </div>
                  </div>
                )
              })}

              {/* ── Now-playing bar ── */}
              {setlistCurrentIndex >= 0 && (
                <div className="setlist-now-playing">
                  <div className="setlist-now-playing__info">
                    <div className="setlist-now-playing__status">
                      {globalIsPlaying
                        ? <><i className="bi bi-play-fill"></i> Now Playing</>
                        : <><i className="bi bi-pause-fill"></i> Paused</>
                      }
                    </div>
                    <div className="setlist-now-playing__title">
                      {setlistTracks[setlistCurrentIndex]?.name}
                    </div>
                    <div className="setlist-now-playing__pos">
                      {setlistCurrentIndex + 1} of {setlistTracks.length}
                    </div>
                  </div>

                  <div className="setlist-now-playing__controls">
                    {/* Repeat toggle */}
                    <button onClick={cycleRepeat} className="setlist-np-btn">
                      <i className={repeatMode === 'one' ? 'bi bi-repeat-1' : 'bi bi-arrow-repeat'}></i>
                    </button>
                    {/* Previous */}
                    <button
                      onClick={() => {
                        if (setlistCurrentIndex > 0) playTrack(setlistCurrentIndex - 1)
                        else if (repeatMode === 'all') playTrack(setlistTracks.length - 1)
                      }}
                      disabled={setlistCurrentIndex === 0 && repeatMode !== 'all'}
                      className="setlist-np-btn"
                    >
                      <i className="bi bi-skip-start-fill"></i>
                    </button>
                    {/* Play / pause */}
                    <button
                      onClick={() => setlistPlaying ? pauseTrack() : playTrack(setlistCurrentIndex >= 0 ? setlistCurrentIndex : 0)}
                      className="setlist-np-btn--play"
                    >
                      <i className={`bi ${setlistPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                    </button>
                    {/* Next */}
                    <button
                      onClick={() => {
                        if (setlistCurrentIndex < setlistTracks.length - 1) playTrack(setlistCurrentIndex + 1)
                        else if (repeatMode === 'all') playTrack(0)
                      }}
                      disabled={setlistCurrentIndex >= setlistTracks.length - 1 && repeatMode !== 'all'}
                      className="setlist-np-btn"
                    >
                      <i className="bi bi-skip-end-fill"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* ── END SETLIST ── */}

        {/* ── Action buttons ── */}
        <div className="action-buttons">
          {!gig.completed && (
            <button className="btn-primary" onClick={handleMarkComplete}>Mark Complete</button>
          )}
          <Link to={`/gigs/edit/${gig.id}`} className="btn-secondary">Edit Gig</Link>

          {setlistTracks.length > 0 && (
            <button className="btn-primary" onClick={() => setShowSendMusicModal(true)}>
              <i className="bi bi-envelope-fill"></i> Send Music
            </button>
          )}

          {/* Calendar export dropdown */}
          <div className="d-inline-block pos-relative" ref={calendarMenuRef}>
            <button
              className="btn-secondary"
              onClick={() => setShowCalendarMenu(prev => !prev)}
              aria-haspopup="true"
              aria-expanded={showCalendarMenu}
            >
              <i className="bi bi-calendar-plus me-1"></i>Add to Calendar
              <i className={`bi bi-chevron-${showCalendarMenu ? 'up' : 'down'} ms-1`}></i>
            </button>
            {showCalendarMenu && (
              <div role="menu" className="cal-export-dropdown">
                <div className="cal-export-header">
                  <i className="bi bi-info-circle me-1"></i>
                  Exports: show name · venue · time · address
                  {gig.age_restriction ? ' · age restriction' : ''}
                  {gig.cover_charge    ? ' · cover charge'   : ''}
                </div>
                {[
                  { label: 'iCalendar (.ics)', sub: 'Apple Calendar · Outlook · any app', icon: 'bi-calendar2-check', action: downloadICS },
                  { label: 'Google Calendar',  sub: 'Opens in a new tab to confirm',       icon: 'bi-google',         action: openGoogleCalendar },
                ].map(item => (
                  <button
                    key={item.label}
                    role="menuitem"
                    onClick={item.action}
                    className="cal-export-btn"
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <div>
                      <div>{item.label}</div>
                      <div className="cal-export-btn-sub">{item.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="btn-danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {/* ── Send Music modal ── */}
      {showSendMusicModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Send Setlist Tracks <i className="bi bi-envelope"></i></h2>
            <div className="send-music-track-list">
              <p className="send-music-track-title">
                {setlistTracks.length} track{setlistTracks.length !== 1 ? 's' : ''} from this setlist:
              </p>
              {setlistTracks.map((t, i) => (
                <div key={t.id} className="send-music-track-item">{i + 1}. {t.name}</div>
              ))}
            </div>
            <div className="form-group">
              <label>Your Name</label>
              <input
                type="text"
                placeholder="Stage name"
                value={sendMusicInfo.performerName}
                onChange={e => setSendMusicInfo(prev => ({ ...prev, performerName: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Show Name</label>
              <input
                type="text"
                placeholder=" Drag Brunch, Viewing Party"
                value={sendMusicInfo.showName}
                onChange={e => setSendMusicInfo(prev => ({ ...prev, showName: e.target.value }))}
              />
            </div>
            {gig.contact_email && (
              <div className="panel-pink-sm mb-2">
                <i className="bi bi-info-circle gig-icon-primary-gap"></i>
                Your email client will open with <strong>{gig.contact_email}</strong> pre-filled as the recipient.
              </div>
            )}
            <p className="preview-text">
              Subject: <strong>
                {sendMusicInfo.performerName || 'Your Name'}'s{' '}
                {setlistTracks.length === 1 ? 'song' : 'songs'} for{' '}
                {sendMusicInfo.showName || 'Show Name'}
              </strong>
            </p>
            <div className="modal-buttons">
              <button
                className="btn-primary"
                disabled={!sendMusicInfo.performerName || !sendMusicInfo.showName}
                onClick={handleSendMusic}
              >
                <i className="bi bi-envelope-fill"></i> Open in Email App
              </button>
              <button className="btn-secondary" onClick={() => setShowSendMusicModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ============================================================
// AddGig
// ============================================================
export function AddGig() {

  const { id }        = useParams()
  const navigate      = useNavigate()
  const { gigs, addGig, updateGig } = useGigs()

  const {
    scheduleForGig,
    checkPendingNotifications,
    scheduling,
  } = useScheduleNotifications()

  const { prepMinutes } = useNotificationPrefs()

  const isEditing   = !!id
  const existingGig = isEditing ? gigs.find(g => g.id === id) : null

  const allVenues = useMemo(() => {
    const seen = new Map()
    gigs
      .filter(g => g.venue_name && g.venue_name.trim())
      .forEach(g => {
        const key = g.venue_name.trim().toLowerCase()
        if (!seen.has(key)) {
          seen.set(key, {
            label:           g.venue_name.trim(),
            venue_name:      g.venue_name      || '',
            venue_address:   g.venue_address   || '',
            venue_lat:       g.venue_lat       ?? null,
            venue_lng:       g.venue_lng       ?? null,
            city:            g.city            || '',
            state:           g.state           || '',
            age_restriction: g.age_restriction || '',
            cover_charge:    g.cover_charge    || '',
          })
        }
      })
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [gigs])

  const [venueSearchQuery,  setVenueSearchQuery]  = useState('')
  const [venueDropdownOpen, setVenueDropdownOpen] = useState(false)
  const venueSearchRef = useRef(null)

  const filteredVenues = useMemo(() => {
    const q = venueSearchQuery.trim().toLowerCase()
    if (!q) return allVenues
    return allVenues.filter(v => v.label.toLowerCase().includes(q))
  }, [allVenues, venueSearchQuery])

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (venueSearchRef.current && !venueSearchRef.current.contains(e.target))
        setVenueDropdownOpen(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const [formData, setFormData] = useState({
    name: '', venue_name: '', venue_address: '', date: '', time: '', call_time: '',
    venue_lat: null, venue_lng: null, payment: '', timezone: 'America/New_York',
    contact_name: '', contact_email: '', contact_phone: '', contact_instagram: '',
    contact_facebook: '', notes: '', city: '', state: '', age_restriction: '',
    cover_charge: '', prep_address: '', prep_lat: null, prep_lng: null,
    errand_address: '', errand_lat: null, errand_lng: null, errand_duration: 30,
    is_managed: false, manager_fee_type: 'pct', manager_fee_pct: '', manager_fee_flat: '',
    is_private: false, ticket_link: '',
  })

  const [readyStep1, setReadyStep1] = useState('home')
  const [readySplit, setReadySplit] = useState('full')

  const [venueSuggestions,     setVenueSuggestions]     = useState([])
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false)
  const [venueSearching,       setVenueSearching]       = useState(false)
  const venueDebounceRef = useRef(null)
  const cityStateDebounceRef = useRef(null)

  const [prepSuggestions,     setPrepSuggestions]     = useState([])
  const [showPrepSuggestions, setShowPrepSuggestions] = useState(false)
  const [prepSearching,       setPrepSearching]       = useState(false)
  const prepDebounceRef = useRef(null)

  const [errandSuggestions,     setErrandSuggestions]     = useState([])
  const [showErrandSuggestions, setShowErrandSuggestions] = useState(false)
  const [errandSearching,       setErrandSearching]       = useState(false)
  const errandDebounceRef = useRef(null)

  const [showErrandInput, setShowErrandInput] = useState(false)

  // ── Contract: inline upload (managed gigs, edit mode only) ────
  // A gig id is required to namespace the storage path, so this control
  // only shows up once the gig already exists (isEditing).
  const [uploadingContract, setUploadingContract] = useState(false)
  const contractInputRef = useRef(null)

  // Stores the IANA tz string set by auto-detect so TimezoneInput
  // can show the plain-text confirmation note below the select.
  // Reset to null if the performer manually changes the timezone.
  const [detectedTzNote, setDetectedTzNote] = useState(null)

  // conflict modal for new gigs: null | { conflicts, gigData, user }
  const [conflictPending, setConflictPending] = useState(null)
  const [conflictSaving,  setConflictSaving]  = useState(false)
  // trip location warning (existing trip mismatch): null | { gigData, user, tripLabel, tripDestination, tripStart, tripEnd }
  const [tripWarning,     setTripWarning]     = useState(null)
  // trip nudge (out-of-state, no existing trip): null | { gigName, venueCity }
  const [tripNudge,       setTripNudge]       = useState(null)

  useEffect(() => {
    if (existingGig) {
      const formattedDate = existingGig.date ? existingGig.date.split('T')[0] : ''
      setFormData({
        name:               existingGig.name               || '',
        venue_name:         existingGig.venue_name         || '',
        venue_address:      existingGig.venue_address      || '',
        date:               formattedDate,
        time:               existingGig.time               || '',
        call_time:          existingGig.call_time          || '',
        venue_lat:          existingGig.venue_lat          ?? null,
        venue_lng:          existingGig.venue_lng          ?? null,
        payment:            existingGig.payment            || '',
        timezone:           existingGig.timezone           || 'America/New_York',
        contact_name:       existingGig.contact_name       || '',
        contact_email:      existingGig.contact_email      || '',
        contact_phone:      existingGig.contact_phone      || '',
        contact_instagram:  existingGig.contact_instagram  || '',
        contact_facebook:   existingGig.contact_facebook   || '',
        notes:              existingGig.notes              || '',
        city:               existingGig.city               || '',
        state:              existingGig.state              || '',
        age_restriction:    existingGig.age_restriction    || '',
        cover_charge:       existingGig.cover_charge       || '',
        prep_address:       existingGig.prep_address       || '',
        prep_lat:           existingGig.prep_lat           ?? null,
        prep_lng:           existingGig.prep_lng           ?? null,
        errand_address:     existingGig.errand_address     || '',
        errand_lat:         existingGig.errand_lat         ?? null,
        errand_lng:         existingGig.errand_lng         ?? null,
        errand_duration:    existingGig.errand_duration    ?? 30,
        is_managed:         existingGig.is_managed         || false,
        manager_fee_type:   existingGig.manager_fee_type   || 'pct',
        manager_fee_pct:    existingGig.manager_fee_pct    != null ? String(existingGig.manager_fee_pct)  : '',
        manager_fee_flat:   existingGig.manager_fee_flat   != null ? String(existingGig.manager_fee_flat) : '',
        is_private:         existingGig.is_private         || false,
        ticket_link:        existingGig.ticket_link        || '',
      })
      const { step1, split } = parseCompoundReadyLocation(existingGig.get_ready_location)
      setReadyStep1(step1)
      setReadySplit(split)
      if (existingGig.errand_address) setShowErrandInput(true)
    }
  }, [existingGig])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // Fires when the performer types directly into the City or State boxes
  // (rather than picking a venue from the address search). Debounced so we
  // don't hit Open-Meteo on every keystroke; only looks up once both city
  // and state have values.
  const handleCityStateChange = (e) => {
    const { name, value } = e.target

    setFormData(prev => {
      const updated = { ...prev, [name]: value }

      if (cityStateDebounceRef.current) clearTimeout(cityStateDebounceRef.current)

      const city  = updated.city
      const state = updated.state
      if (city && state) {
        cityStateDebounceRef.current = setTimeout(() => {
          tzFromOpenMeteo(city, state).then(tz => {
            if (tz) {
              setFormData(p => ({ ...p, timezone: tz }))
              setDetectedTzNote(tz)
            }
          })
        }, 500)
      } else {
        // Incomplete pair -- clear any prior auto-detect note since it no
        // longer reflects what's in the boxes.
        setDetectedTzNote(null)
      }

      return updated
    })
  }

  const handlePastVenueSelect = (venue) => {
    setFormData(prev => ({
      ...prev,
      venue_name:      venue.venue_name,
      venue_address:   venue.venue_address,
      venue_lat:       venue.venue_lat,
      venue_lng:       venue.venue_lng,
      city:            venue.city,
      state:           venue.state,
      age_restriction: venue.age_restriction,
      cover_charge:    venue.cover_charge,
    }))
    setVenueSuggestions([])
    setShowVenueSuggestions(false)

    // Auto-detect timezone from city + state via Open-Meteo geocoding API.
    tzFromOpenMeteo(venue.city, venue.state).then(tz => {
      if (tz) {
        setFormData(prev => ({ ...prev, timezone: tz }))
        setDetectedTzNote(tz)
      }
    })
  }

  const handleShowTimeChange = (e) => {
    const showTime = e.target.value
    setFormData(prev => {
      const updates = { ...prev, time: showTime }
      if (!prev.call_time && showTime) {
        const [hours, minutes] = showTime.split(':').map(Number)
        const totalMins        = hours * 60 + minutes - 60
        const callTotalMins    = ((totalMins % (24 * 60)) + 24 * 60) % (24 * 60)
        updates.call_time = String(Math.floor(callTotalMins / 60)).padStart(2, '0') + ':' + String(callTotalMins % 60).padStart(2, '0')
      }
      return updates
    })
  }

  const handleVenueNameChange = (e) => {
    const value = e.target.value

    // Clear the auto-detected timezone note whenever the venue name
    // is manually edited. If they pick a new Nominatim result the
    // timezone will re-detect. If they type without picking, the
    // timezone resets to the default rather than silently keeping
    // the previous venue's timezone.
    setDetectedTzNote(null)
    if (detectedTzNote) {
      setFormData(prev => ({ ...prev, timezone: 'America/New_York' }))
    }

    setFormData(prev => ({ ...prev, venue_name: value }))
    if (venueDebounceRef.current) clearTimeout(venueDebounceRef.current)
    if (value.trim().length < 3) { setVenueSuggestions([]); setShowVenueSuggestions(false); return }
    venueDebounceRef.current = setTimeout(async () => {
      setVenueSearching(true)
      try {
        const params = new URLSearchParams({ q: value, format: 'json', limit: '5', addressdetails: '1' })
        const res    = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'User-Agent': NOMINATIM_USER_AGENT } })
        if (!res.ok) throw new Error()
        const results = await res.json()
        setVenueSuggestions(results)
        setShowVenueSuggestions(results.length > 0)
      } catch { setVenueSuggestions([]); setShowVenueSuggestions(false) }
      finally { setVenueSearching(false) }
    }, 400)
  }

  const handleVenueSelect = (result) => {
    const addr       = result.address || {}
    const venueName  = result.name || result.display_name.split(',')[0].trim()
    const streetLine = addr.house_number && addr.road ? `${addr.house_number} ${addr.road}` : addr.road || ''
    const cityLine   = addr.city || addr.town || addr.village || addr.municipality || ''
    const addressParts = [streetLine, cityLine, addr.state || '', addr.postcode || ''].filter(Boolean)
    const address      = addressParts.length > 0 ? addressParts.join(', ') : result.display_name.split(',').slice(0, 4).join(',').trim()

    setFormData(prev => ({
      ...prev,
      venue_name:    venueName,
      venue_address: address,
      city:          cityLine,
      state:         addr.state || '',
    }))
    setVenueSuggestions([])
    setShowVenueSuggestions(false)

    // Auto-detect timezone from city + state via Open-Meteo geocoding API.
    // Free, no API key, CORS-enabled. Fire-and-forget so the form fills
    // instantly and the timezone select updates when the fetch resolves.
    tzFromOpenMeteo(cityLine, addr.state).then(tz => {
      if (tz) {
        setFormData(prev => ({ ...prev, timezone: tz }))
        setDetectedTzNote(tz)
      }
    })
  }

  const handlePrepAddressChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, prep_address: value, prep_lat: null, prep_lng: null }))
    if (prepDebounceRef.current) clearTimeout(prepDebounceRef.current)
    if (value.trim().length < 3) { setPrepSuggestions([]); setShowPrepSuggestions(false); return }
    prepDebounceRef.current = setTimeout(async () => {
      setPrepSearching(true)
      try {
        const params  = new URLSearchParams({ q: value, format: 'json', limit: '5', addressdetails: '1' })
        const res     = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'User-Agent': NOMINATIM_USER_AGENT } })
        if (!res.ok) throw new Error()
        const results = await res.json()
        setPrepSuggestions(results)
        setShowPrepSuggestions(results.length > 0)
      } catch { setPrepSuggestions([]); setShowPrepSuggestions(false) }
      finally { setPrepSearching(false) }
    }, 400)
  }

  const handlePrepSelect = (result) => {
    const addr     = result.address || {}
    const namePart = result.name || result.display_name.split(',')[0].trim()
    const street   = addr.house_number && addr.road ? `${addr.house_number} ${addr.road}` : addr.road || ''
    const city     = addr.city || addr.town || addr.village || ''
    const parts    = [namePart, street, city, addr.state || ''].filter((p, i) => p && (i < 2 || p !== namePart))
    const label    = parts.slice(0, 3).join(', ') || result.display_name.split(',').slice(0, 3).join(',').trim()
    setFormData(prev => ({ ...prev, prep_address: label, prep_lat: parseFloat(result.lat), prep_lng: parseFloat(result.lon) }))
    setPrepSuggestions([])
    setShowPrepSuggestions(false)
  }

  const handleErrandAddressChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, errand_address: value, errand_lat: null, errand_lng: null }))
    if (errandDebounceRef.current) clearTimeout(errandDebounceRef.current)
    if (value.trim().length < 3) { setErrandSuggestions([]); setShowErrandSuggestions(false); return }
    errandDebounceRef.current = setTimeout(async () => {
      setErrandSearching(true)
      try {
        const params  = new URLSearchParams({ q: value, format: 'json', limit: '5', addressdetails: '1' })
        const res     = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'User-Agent': NOMINATIM_USER_AGENT } })
        if (!res.ok) throw new Error()
        const results = await res.json()
        setErrandSuggestions(results)
        setShowErrandSuggestions(results.length > 0)
      } catch { setErrandSuggestions([]); setShowErrandSuggestions(false) }
      finally { setErrandSearching(false) }
    }, 400)
  }

  const handleErrandSelect = (result) => {
    const addr     = result.address || {}
    const namePart = result.name || result.display_name.split(',')[0].trim()
    const street   = addr.house_number && addr.road ? `${addr.house_number} ${addr.road}` : addr.road || ''
    const city     = addr.city || addr.town || addr.village || ''
    const parts    = [namePart, street, city, addr.state || ''].filter((p, i) => p && (i < 2 || p !== namePart))
    const label    = parts.slice(0, 3).join(', ') || result.display_name.split(',').slice(0, 3).join(',').trim()
    setFormData(prev => ({ ...prev, errand_address: label, errand_lat: parseFloat(result.lat), errand_lng: parseFloat(result.lon) }))
    setErrandSuggestions([])
    setShowErrandSuggestions(false)
  }

  // ── doSave: performs the actual insert/update after conflicts are cleared ──
  // gigData = the fully assembled gig object ready to write to Supabase
  // user    = the currently logged-in user (needed for scheduling)
  const doSave = async (gigData, user) => {
    try {
      if (isEditing) {
        await supabase.from('scheduled_notifications').delete().eq('gig_id', id)
        await updateGig(id, gigData)
        await scheduleForGig(id, { ...gigData, id }, user.id)
        await checkPendingNotifications()
        navigate('/gigs')
      } else {
        const insertedGig = await addGig(gigData)
        if (insertedGig?.id) {
          await scheduleForGig(insertedGig.id, insertedGig, user.id)
          await checkPendingNotifications()
        } else {
          console.warn('[AddGig] addGig did not return the inserted row. Notifications not scheduled.')
        }

        // Check if the venue is 1.5+ hours from the performer's home address.
        // If so, nudge them to plan a trip.
        //
        // Steps:
        //   1. If venue_lat/venue_lng are missing (user typed address without
        //      picking a Nominatim suggestion), geocode venue_address as a
        //      fallback so the distance check still works.
        //   2. Geocode user_metadata.location ('City, State') via Nominatim
        //      to get home lat/lng.
        //   3. Hit OSRM for a driving duration between the two points.
        //   4. If the route is >= 5400 s (1.5 hrs), show the nudge modal.
        //      If no coords, routing fails, or drive time is under 1.5 hrs,
        //      skip the nudge and navigate normally -- this check is non-fatal.
        let showedNudge = false
        const homeRaw = user.user_metadata?.location || ''

        // Step 1: resolve venue coords, falling back to geocoding the address
        // string if the user didn't pick from the Nominatim dropdown.
        let resolvedVenueLat = gigData.venue_lat
        let resolvedVenueLng = gigData.venue_lng

        if (homeRaw && (!resolvedVenueLat || !resolvedVenueLng) && gigData.venue_address) {
          try {
            const venueGeoParams = new URLSearchParams({
              q: gigData.venue_address, format: 'json', limit: '1',
            })
            const venueGeoRes  = await fetch(
              `https://nominatim.openstreetmap.org/search?${venueGeoParams}`,
              { headers: { 'User-Agent': NOMINATIM_USER_AGENT } },
            )
            const venueGeoData = await venueGeoRes.json()
            if (venueGeoData.length > 0) {
              resolvedVenueLat = parseFloat(venueGeoData[0].lat)
              resolvedVenueLng = parseFloat(venueGeoData[0].lon)
            }
          } catch (venueGeoErr) {
            console.warn('[AddGig] Venue geocode fallback failed (non-fatal):', venueGeoErr)
          }
        }

        // Step 2-4: geocode home, call OSRM, show nudge if >= 1.5 hrs.
        if (homeRaw && resolvedVenueLat && resolvedVenueLng) {
          try {
            const geocodeParams = new URLSearchParams({
              q: homeRaw, format: 'json', limit: '1',
            })
            const geocodeRes = await fetch(
              `https://nominatim.openstreetmap.org/search?${geocodeParams}`,
              { headers: { 'User-Agent': NOMINATIM_USER_AGENT } },
            )
            const geocodeData = await geocodeRes.json()

            if (geocodeData.length > 0) {
              const homeLat = parseFloat(geocodeData[0].lat)
              const homeLng = parseFloat(geocodeData[0].lon)

              // OSRM public routing API: coordinates are lng,lat order.
              const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${homeLng},${homeLat};${resolvedVenueLng},${resolvedVenueLat}?overview=false`
              const osrmRes  = await fetch(osrmUrl)
              const osrmData = await osrmRes.json()

              // duration is in seconds; 1.5 hours = 5400 seconds.
              const durationSecs = osrmData?.routes?.[0]?.duration
              if (typeof durationSecs === 'number' && durationSecs >= 5400) {
                setTripNudge({
                  gigName:   gigData.name || gigData.venue_name || 'Your gig',
                  venueCity: [gigData.city, gigData.state].filter(Boolean).join(', ') || gigData.venue_address || '',
                })
                showedNudge = true
              }
            }
          } catch (distErr) {
            // Non-fatal: routing or geocoding failed, skip nudge entirely.
            console.warn('[AddGig] Trip nudge distance check failed (non-fatal):', distErr)
          }
        }

        if (!showedNudge) navigate('/gigs')
      }
    } catch (error) {
      alert('Error saving gig: ' + error.message)
    }
  }

  const handleContractFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !isEditing) return
    if (!isAllowedContractFile(file)) {
      alert('Contracts must be a PDF, DOC, or DOCX file.')
      return
    }
    setUploadingContract(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { alert('Not logged in.'); return }
      const url = await uploadContractFile(id, user.id, file)
      await updateGig(id, { contract_url: url })
    } catch (error) {
      alert('Error uploading contract: ' + error.message)
    } finally {
      setUploadingContract(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { alert('Not logged in.'); return }

      const get_ready_location = buildCompoundReadyLocation(readyStep1, readySplit)
      const prepAddress = readyStep1 === 'other' ? formData.prep_address : null
      const prepLat     = readyStep1 === 'other' ? formData.prep_lat     : null
      const prepLng     = readyStep1 === 'other' ? formData.prep_lng     : null

      let times = null
      if (formData.venue_address && formData.time) {
        try {
          times = await calculateGigTimes(
            formData.date, formData.time, prepMinutes,
            formData.venue_address, get_ready_location, prepAddress, formData.call_time || null,
          )
        } catch (routingErr) {
          console.warn('[AddGig] Routing failed (non-fatal):', routingErr)
        }
      }

      const fallback = isEditing ? existingGig : null
      const gigData = {
        ...formData,
        prep_address:       prepAddress,
        prep_lat:           prepLat,
        prep_lng:           prepLng,
        get_ready_location,
        leave_time:         times?.leaveTime       ?? fallback?.leave_time        ?? null,
        makeup_start_time:  times?.makeupStartTime ?? fallback?.makeup_start_time ?? null,
        leave_home_time:    times?.leaveHomeTime   ?? fallback?.leave_home_time   ?? null,
        is_managed:         formData.is_managed,
        manager_fee_type:   formData.is_managed ? formData.manager_fee_type : null,
        manager_fee_pct:    formData.is_managed && formData.manager_fee_type === 'pct'  ? (parseFloat(formData.manager_fee_pct)  || null) : null,
        manager_fee_flat:   formData.is_managed && formData.manager_fee_type === 'flat' ? (parseFloat(formData.manager_fee_flat) || null) : null,
        is_private:         formData.is_private,
        ticket_link:        formData.ticket_link || null,
      }

      // ── Conflict check (new gigs only) ─────────────────────
      if (!isEditing && formData.date) {
        const { data: existing } = await supabase
          .from('gigs')
          .select('id, name, date, time, venue_name')
          .eq('user_id', user.id)
          .eq('date', formData.date)

        if (existing?.length) {
          setConflictPending({ conflicts: existing, gigData, user })
          return
        }
      }

      // ── Trip location mismatch check (new gigs only) ───────
      if (!isEditing && formData.date) {
        try {
          const { data: prefs } = await supabase
            .from('notification_preferences')
            .select('trips')
            .eq('user_id', user.id)
            .single()

          const trips = prefs?.trips || []
          const norm  = (s) => (s || '').toLowerCase().trim()
          const gigCity = norm(formData.city || formData.venue_address || '')

          for (const trip of trips) {
            let tripStart, tripEnd
            if (trip.trip_type === 'multi_leg') {
              const legs = trip.legs || []
              const dates = [...legs.map(l => l.depart_at?.split('T')[0]), ...legs.map(l => l.arrive_at?.split('T')[0])].filter(Boolean).sort()
              tripStart = dates[0]; tripEnd = dates.at(-1)
            } else {
              tripStart = trip.depart_home_at?.split('T')[0]
              tripEnd   = trip.arrive_home_at?.split('T')[0]
            }
            if (!tripStart || !tripEnd) continue
            if (formData.date < tripStart || formData.date > tripEnd) continue

            const dest = norm(trip.destination_label || '')
            if (dest && (gigCity.includes(dest) || gigCity.includes(dest.split(',')[0]))) continue

            setTripWarning({
              gigData, user,
              tripLabel:       trip.destination_label || 'your trip',
              tripDestination: trip.destination_label || '',
              tripStart, tripEnd,
            })
            return
          }
        } catch (tripErr) {
          console.warn('[AddGig] Trip check failed (non-fatal):', tripErr)
        }
      }

      await doSave(gigData, user)

    } catch (error) {
      alert('Error saving gig: ' + error.message)
    }
  }

  async function handleConflictSaveAnyway() {
    if (!conflictPending) return
    const { gigData, user } = conflictPending
    setConflictSaving(true)
    setConflictPending(null)
    await doSave(gigData, user)
    setConflictSaving(false)
  }

  async function handleTripSaveAnyway() {
    if (!tripWarning) return
    const { gigData, user } = tripWarning
    setTripWarning(null)
    await doSave(gigData, user)
  }

  return (
    <div className="add-gig-page">
      <Link to="/gigs" className="back-link">← Back to Gigs</Link>
      <h1 className="page-title">{isEditing ? 'Edit Gig' : 'Add New Gig'}</h1>

      <div className="gig-form">

        <div className="form-group">
          <label>Gig Name *</label>
          <input type="text" name="name" placeholder=" Drag Brunch" value={formData.name} onChange={handleChange} required />
        </div>

        {/* ── Quick-fill venue search ── */}
        {allVenues.length > 0 && (
          <div className="form-group">
            <label htmlFor="venue-search">Quick-Fill Venue</label>
            <div ref={venueSearchRef} className="venue-search-wrapper">
              <div className="venue-search-wrapper">
                <i className="bi bi-geo-alt-fill venue-search-icon" />
                <input
                  id="venue-search"
                  type="text"
                  autoComplete="off"
                  placeholder="Search venues..."
                  value={venueSearchQuery}
                  onChange={e => { setVenueSearchQuery(e.target.value); setVenueDropdownOpen(true) }}
                  onFocus={() => setVenueDropdownOpen(true)}
                  className="venue-search-input"
                />
                <button
                  type="button"
                  onClick={() => setVenueDropdownOpen(prev => !prev)}
                  className="venue-search-toggle"
                >
                  <i className={venueDropdownOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
                </button>
              </div>
              {venueDropdownOpen && (
                <ul role="listbox" className="venue-dropdown">
                  {filteredVenues.length === 0 ? (
                    <li className="venue-dropdown-empty">No matching venues</li>
                  ) : (
                    filteredVenues.map(venue => (
                      <li
                        key={venue.label}
                        role="option"
                        aria-selected={false}
                        onMouseDown={() => { handlePastVenueSelect(venue); setVenueSearchQuery(''); setVenueDropdownOpen(false) }}
                        className="venue-dropdown-item"
                      >
                        <i className="bi bi-geo-alt-fill gig-icon-primary-shrink" />
                        <span className="venue-dropdown-item-name">{venue.label}</span>
                        {venue.venue_address && (
                          <span className="venue-dropdown-item-addr">{venue.venue_address}</span>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <small>Search or browse all your venues.</small>
          </div>
        )}

        {/* ── Venue name (Nominatim search) ── */}
        <div className="form-group">
          <label>Venue Name *</label>
          <div className="venue-search-wrapper">
            <input
              type="text"
              name="venue_name"
              placeholder=" The Eagle"
              value={formData.venue_name}
              onChange={handleVenueNameChange}
              onBlur={() => { setTimeout(() => setShowVenueSuggestions(false), 150) }}
              onFocus={() => { if (venueSuggestions.length > 0) setShowVenueSuggestions(true) }}
              required
              autoComplete="off"
            />
            {venueSearching && (
              <span className="input-search-spinner">
                <i className="bi bi-arrow-repeat spin"></i>
              </span>
            )}
            {showVenueSuggestions && venueSuggestions.length > 0 && (
              <ul className="nominatim-dropdown">
                {venueSuggestions.map((result, i) => (
                  <li
                    key={result.place_id || i}
                    onMouseDown={() => handleVenueSelect(result)}
                    className="nominatim-dropdown-item"
                  >
                    <div className="nominatim-item-name">{result.name || result.display_name.split(',')[0].trim()}</div>
                    <div className="nominatim-item-full">{result.display_name}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <small>Start typing to search. Selecting a result fills in the address below.</small>
        </div>

        <div className="form-group">
          <label>Venue Address</label>
          <input
            type="text"
            name="venue_address"
            placeholder="Full address"
            value={formData.venue_address}
            onChange={e => {
              const address = e.target.value
              const { city, state } = parseCityStateFromAddress(address)

              // Clear auto-detected timezone when address is manually edited
              setDetectedTzNote(null)
              if (detectedTzNote) {
                setFormData(prev => ({ ...prev, timezone: 'America/New_York' }))
              }

              setFormData(prev => ({
                ...prev,
                venue_address: address,
                ...(city  ? { city }  : {}),
                ...(state ? { state } : {}),
              }))
            }}
          />
        </div>

        {/* ── Promo Info ── */}
        <h3>Promo Info</h3>
        <p className="promo-section-hint">
          Used on your monthly promo poster. City and state fill in automatically when you select a venue above.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input type="text" name="city" placeholder="Auto-filled from venue" value={formData.city} onChange={handleCityStateChange} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input type="text" name="state" placeholder="Auto-filled from venue" value={formData.state} onChange={handleCityStateChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Age Restriction</label>
            <select name="age_restriction" value={formData.age_restriction} onChange={handleChange}>
              <option value="">-- Not specified --</option>
              <option value="All Ages">All Ages</option>
              <option value="18+">18+</option>
              <option value="21+">21+</option>
            </select>
          </div>
          <div className="form-group">
            <label>Cover Charge</label>
            <input type="text" name="cover_charge" placeholder=" Free, $10, $5-$15" value={formData.cover_charge} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Ticket / RSVP Link</label>
            <input type="url" name="ticket_link" placeholder="https://" value={formData.ticket_link} onChange={handleChange} />
            <small>Optional. Link to ticket page or RSVP.</small>
          </div>
        </div>

        {/* Date, Show Time, Call Time, Timezone -- rendered via shared FormInputs components */}
          <DateInput
        label="Date *"
        value={formData.date}
        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
      />
        <TimeInput
          label="Show Time *"
          value={formData.time}
          onChange={handleShowTimeChange}
          hint="The show time on the flyer"
        />
       <TimeInput
          label="Call Time"
          value={formData.call_time}
          onChange={(e) => setFormData(prev => ({ ...prev, call_time: e.target.value }))}
          hint="When you need to arrive. Defaults to 1 hour before show time."
        />
        <TimezoneInput
          label="Timezone *"
          name="timezone"
          value={formData.timezone}
          onChange={(e) => { handleChange(e); setDetectedTzNote(null) }}
          required
          autoDetectNote={detectedTzNote ? `Timezone set to ${detectedTzNote} from venue location. Change if needed.` : null}
        />

        <div className="form-row">
          <div className="form-group">
            <label>Payment ($)</label>
            <input type="number" name="payment" placeholder="0.00" value={formData.payment} onChange={handleChange} />
          </div>
        </div>

        {/* ── MANAGED GIG ── */}
        <div className="add-gig-managed-panel">
          <label className="gig-checkbox-label">
            <input
              type="checkbox"
              checked={formData.is_managed}
              onChange={e => setFormData(prev => ({ ...prev, is_managed: e.target.checked }))}
              className="gig-check"
            />
            <span>
              <i className="bi bi-briefcase-fill dash-section-icon"></i>This is a managed gig
            </span>
          </label>

          {!formData.is_managed && (
            <p className="add-gig-managed-hint">
              Booked through an agent or manager? Tick the box to track their fee and see your take-home pay.
            </p>
          )}

          {formData.is_managed && (
            <div className="add-gig-managed-fields">

              <div className="form-group mb-0">
                <label>Manager fee type</label>
                <div className="manager-fee-type-group">
                  {[
                    { value: 'pct',  label: '% of booking' },
                    { value: 'flat', label: 'Flat amount'   },
                  ].map(opt => (
                    <label
                      key={opt.value}
                      className={`fee-type-label${formData.manager_fee_type === opt.value ? ' fee-type-label--active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="manager_fee_type"
                        value={opt.value}
                        checked={formData.manager_fee_type === opt.value}
                        onChange={() => setFormData(prev => ({ ...prev, manager_fee_type: opt.value }))}
                        className="gig-fee-radio"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {formData.manager_fee_type === 'pct' ? (
                <div className="form-group mb-0">
                  <label>Manager percentage (%)</label>
                  <div className="d-flex align-items-center gig-flex-wrap-gap">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder=" 15"
                      value={formData.manager_fee_pct}
                      onChange={e => setFormData(prev => ({ ...prev, manager_fee_pct: e.target.value }))}
                      className="manager-fee-input"
                    />
                    <span className="manager-fee-unit">%</span>
                    {formData.manager_fee_pct && formData.payment && (
                      <span className="manager-fee-live">
                        = ${calcFee(formData.payment, 'pct', formData.manager_fee_pct, null).toFixed(2)} fee
                        &nbsp;·&nbsp; you keep ${(parseFloat(formData.payment) - calcFee(formData.payment, 'pct', formData.manager_fee_pct, null)).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <small>Enter the percentage your manager takes from each booking.</small>
                </div>
              ) : (
                <div className="form-group mb-0">
                  <label>Manager flat fee ($)</label>
                  <div className="d-flex align-items-center gig-flex-wrap-gap">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder=" 50"
                      value={formData.manager_fee_flat}
                      onChange={e => setFormData(prev => ({ ...prev, manager_fee_flat: e.target.value }))}
                      className="manager-fee-input"
                    />
                    <span className="manager-fee-unit">$</span>
                    {formData.manager_fee_flat && formData.payment && (
                      <span className="manager-fee-live">
                        you keep ${(parseFloat(formData.payment || 0) - parseFloat(formData.manager_fee_flat || 0)).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <small>Enter the flat dollar amount your manager takes from this booking.</small>
                </div>
              )}

              {isEditing && (
                <div className="form-group mb-0">
                  <label>Contract</label>
                  {existingGig?.contract_url ? (
                    <div className="d-flex align-items-center gig-flex-wrap-gap">
                      <a href={existingGig.contract_url} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                        <i className="bi bi-eye-fill"></i> View Contract
                      </a>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => contractInputRef.current?.click()}
                        disabled={uploadingContract}
                      >
                        <i className={`bi ${uploadingContract ? 'bi-arrow-repeat spin' : 'bi-arrow-repeat'}`}></i>
                        {uploadingContract ? ' Uploading...' : ' Replace'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => contractInputRef.current?.click()}
                      disabled={uploadingContract}
                    >
                      <i className={`bi ${uploadingContract ? 'bi-arrow-repeat spin' : 'bi-file-earmark-plus'}`}></i>
                      {uploadingContract ? ' Uploading...' : ' Upload Contract'}
                    </button>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    ref={contractInputRef}
                    onChange={handleContractFileChange}
                    className="gig-hidden-file-input"
                    style={{ display: 'none' }}
                  />
                  <small>Uploads immediately and attaches to this gig. Doesn't require saving the form.</small>
                </div>
              )}

            </div>
          )}
        </div>

        {/* ── Contact Info ── */}
        <h3>Contact Info</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Contact Name</label>
            <input type="text" name="contact_name" placeholder="Who booked you?" value={formData.contact_name} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="contact_email" placeholder="contact@email.com" value={formData.contact_email} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" name="contact_phone" placeholder="(123) 456-7890" value={formData.contact_phone} onChange={handleChange} />
          </div>
        </div>
        <div className="form-group">
          <label>Instagram</label>
          <input type="text" name="contact_instagram" placeholder="@username" value={formData.contact_instagram} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Facebook</label>
          <input type="text" name="contact_facebook" placeholder="Facebook profile link" value={formData.contact_facebook} onChange={handleChange} />
        </div>

        {/* ── Getting Ready & Travel ── */}
        <h3>
          <i className="bi bi-stars dash-section-icon"></i>
          Getting Ready &amp; Travel
        </h3>
        <p className="add-gig-ready-hint">
          Tell us how you're getting ready for this show. The dashboard uses this to calculate when to leave and sends your get-ready reminder at the right time.
        </p>

        <div className="form-group">
          <label className="mb-2 d-block">Step 1: Where are you starting from?</label>

          <label className={`radio-card${readyStep1 === 'home' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
            <input
              type="radio"
              name="get-ready-location"
              value="home"
              checked={readyStep1 === 'home'}
              onChange={() => setReadyStep1('home')}
              className="gig-radio-input"
            />
            <div>
              <div className="radio-card__title"><i className="bi bi-house-fill dash-section-icon"></i>Home</div>
              <div className="radio-card__desc">Starting from your 'home address' or your 'trip home address'.</div>
            </div>
          </label>

          <label className={`radio-card${readyStep1 === 'other' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
            <input
              type="radio"
              name="get-ready-location"
              value="other"
              checked={readyStep1 === 'other'}
              onChange={() => setReadyStep1('other')}
              className="gig-radio-input"
            />
            <div>
              <div className="radio-card__title"><i className="bi bi-house-heart-fill dash-section-icon"></i>Another location</div>
              <div className="radio-card__desc">Hotel, friend's place, studio, or anywhere that isn't your usual home address.</div>
            </div>
          </label>

          <label className={`radio-card radio-card--last${readyStep1 === 'venue' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
            <input
              type="radio"
              name="get-ready-location"
              value="venue"
              checked={readyStep1 === 'venue'}
              onChange={() => setReadyStep1('venue')}
              className="gig-radio-input"
            />
            <div>
              <div className="radio-card__title"><i className="bi bi-geo-alt-fill dash-section-icon"></i>At the venue</div>
              <div className="radio-card__desc">Driving to the venue first, then getting ready on arrival.</div>
            </div>
          </label>
        </div>

        {/* ── Prep address (other location) ── */}
        {readyStep1 === 'other' && (
          <div className="form-group">
            <label>
              <i className="bi bi-house-heart-fill dash-section-icon"></i>
              Address of where you are getting-ready *
            </label>
            <div className="venue-search-wrapper">
              <input
                type="text"
                placeholder="Hotel name, friend's address, studio..."
                value={formData.prep_address}
                onChange={handlePrepAddressChange}
                onBlur={() => { setTimeout(() => setShowPrepSuggestions(false), 150) }}
                onFocus={() => { if (prepSuggestions.length > 0) setShowPrepSuggestions(true) }}
                autoComplete="off"
              />
              {prepSearching && (
                <span className="input-search-spinner">
                  <i className="bi bi-arrow-repeat spin"></i>
                </span>
              )}
              {showPrepSuggestions && prepSuggestions.length > 0 && (
                <ul className="nominatim-dropdown">
                  {prepSuggestions.map((result, i) => (
                    <li
                      key={result.place_id || i}
                      onMouseDown={() => handlePrepSelect(result)}
                      className="nominatim-dropdown-item"
                    >
                      <div className="nominatim-item-name">{result.name || result.display_name.split(',')[0].trim()}</div>
                      <div className="nominatim-item-full">{result.display_name}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formData.prep_lat && formData.prep_lng && (
              <div className="geocoded-badge">
                <i className="bi bi-check-circle-fill"></i>Location found! The dashboard will route from here.
              </div>
            )}
            <small>Start typing and select from the list.</small>
          </div>
        )}

        {/* ── Prep split (Step 2) ── */}
        {readyStep1 !== 'venue' && (
          <div className="form-group">
            <label className="mb-2 d-block">
              Step 2: How much prep happens {readyStep1 === 'other' ? 'at that location' : 'at home'} before you leave?
            </label>

            <label className={`radio-card${readySplit === 'full' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
              <input
                type="radio"
                name="prep-split"
                value="full"
                checked={readySplit === 'full'}
                onChange={() => setReadySplit('full')}
                className="gig-radio-input"
              />
              <div>
                <div className="radio-card__title"><i className="bi bi-stars dash-section-icon"></i>Fully ready before leaving</div>
                <div className="radio-card__desc">
                  {readyStep1 === 'other'
                    ? 'All hair, makeup, and costume done at this location.'
                    : 'All hair, makeup, and costume done at home.'}
                </div>
              </div>
            </label>

            <label className={`radio-card${readySplit === 'half' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
              <input
                type="radio"
                name="prep-split"
                value="half"
                checked={readySplit === 'half'}
                onChange={() => setReadySplit('half')}
                className="gig-radio-input"
              />
              <div>
                <div className="radio-card__title"><i className="bi bi-slash-circle dash-section-icon"></i>Half and half</div>
                <div className="radio-card__desc">50% prep before leaving, finish the other 50% at the venue.</div>
              </div>
            </label>

            <label className={`radio-card radio-card--last${readySplit === 'most' ? ' radio-card--selected' : ' radio-card--unselected'}`}>
              <input
                type="radio"
                name="prep-split"
                value="most"
                checked={readySplit === 'most'}
                onChange={() => setReadySplit('most')}
                className="gig-radio-input"
              />
              <div>
                <div className="radio-card__title"><i className="bi bi-percent dash-section-icon"></i>Mostly ready, finishing at venue</div>
                <div className="radio-card__desc">70% done before leaving. Last 30% at the venue: body, nails, contacts, lashes.</div>
              </div>
            </label>
          </div>
        )}

        {/* ── ERRAND ── */}
        <div className="add-gig-errand-panel">
          <label className="gig-checkbox-label">
            <input
              type="checkbox"
              checked={showErrandInput || !!formData.errand_address}
              onChange={e => {
                if (!e.target.checked) {
                  setFormData(prev => ({ ...prev, errand_address: '', errand_lat: null, errand_lng: null, errand_duration: 30 }))
                  setErrandSuggestions([])
                }
                setShowErrandInput(e.target.checked)
              }}
              className="gig-check"
            />
            <span><i className="bi bi-bag-fill dash-section-icon"></i>I have an errand before this show</span>
          </label>

          {!showErrandInput && !formData.errand_address && (
            <p className="add-gig-errand-hint">
              Dry cleaner, pharmacy, prop pick-up, snack shopping... tick the box to add a stop to your route.
            </p>
          )}

          {(showErrandInput || !!formData.errand_address) && (
            <div className="add-gig-errand-fields">

              <div className="form-group mb-2">
                <label>Errand address</label>
                <div className="venue-search-wrapper">
                  <input
                    type="text"
                    placeholder=" CVS Pharmacy, 123 Main St"
                    value={formData.errand_address}
                    onChange={handleErrandAddressChange}
                    onBlur={() => { setTimeout(() => setShowErrandSuggestions(false), 150) }}
                    onFocus={() => { if (errandSuggestions.length > 0) setShowErrandSuggestions(true) }}
                    autoComplete="off"
                  />
                  {errandSearching && (
                    <span className="input-search-spinner">
                      <i className="bi bi-arrow-repeat spin"></i>
                    </span>
                  )}
                  {showErrandSuggestions && errandSuggestions.length > 0 && (
                    <ul className="nominatim-dropdown">
                      {errandSuggestions.map((result, i) => (
                        <li
                          key={result.place_id || i}
                          onMouseDown={() => handleErrandSelect(result)}
                          className="nominatim-dropdown-item"
                        >
                          <div className="nominatim-item-name">{result.name || result.display_name.split(',')[0].trim()}</div>
                          <div className="nominatim-item-full">{result.display_name}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {formData.errand_lat && formData.errand_lng && (
                  <div className="geocoded-badge">
                    <i className="bi bi-check-circle-fill"></i>Location found. Added to your route.
                  </div>
                )}
                <small>Start typing and select from the list so we can route through here.</small>
              </div>

              <div className="form-group mb-0">
                <label>How long is the errand?</label>
                <div className="add-gig-duration-row">
                  <input
                    type="number"
                    name="errand_duration"
                    min="5"
                    max="480"
                    step="5"
                    value={formData.errand_duration}
                    onChange={handleChange}
                    className="errand-duration-input"
                  />
                  <span className="add-gig-duration-unit">minutes</span>
                </div>
                <small>Estimate how long you'll spend there.</small>
              </div>

            </div>
          )}
        </div>

        <div className="form-group">
          <label className="gig-checkbox-label">
            <input
              type="checkbox"
              checked={formData.is_private}
              onChange={e => setFormData(prev => ({ ...prev, is_private: e.target.checked }))}
              className="gig-check"
            />
            <span>
              <i className="bi bi-lock-fill dash-section-icon"></i> Private gig
            </span>
          </label>
          <p className="add-gig-managed-hint">If private, this gig won't be publicly listed.</p>
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea name="notes" placeholder="Set list, outfit, equipment needed..." value={formData.notes} onChange={handleChange} rows="4" />
        </div>

        <div className="add-gig-form-actions">
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={scheduling || conflictSaving}>
            {scheduling || conflictSaving ? 'Saving...' : (isEditing ? 'Update Gig' : 'Save Gig')}
          </button>
          <Link to="/gigs" className="btn-secondary">Cancel</Link>
        </div>

      </div>

      {/* ── CONFLICT MODAL ── */}
      {conflictPending && (
        <div
          className="app-modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) setConflictPending(null) }}
        >
          <div className="conflict-modal">

            <div className="app-modal-header">
              <div className="app-modal-header__left">
                <i className="bi bi-exclamation-triangle-fill gig-icon-tertiary2-lg"></i>
                <span className="app-modal-header__title">Date Conflict</span>
              </div>
              <button type="button" onClick={() => setConflictPending(null)} className="app-modal-header__close">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="app-modal-body">
              <p>
                You already have {conflictPending.conflicts.length === 1 ? 'a gig' : 'gigs'} on{' '}
                <strong>
                  {new Date(formData.date + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                  })}
                </strong>:
              </p>
              <div className="conflict-modal__list">
                {conflictPending.conflicts.map(c => (
                  <div key={c.id} className="conflict-item">
                    <div className="conflict-item__name">{c.name || c.venue_name || 'Untitled Gig'}</div>
                    {c.venue_name && c.name && (
                      <div className="conflict-item__meta">
                        <i className="bi bi-geo-alt me-1"></i>{c.venue_name}
                      </div>
                    )}
                    {c.time && (
                      <div className="conflict-item__meta">
                        <i className="bi bi-clock me-1"></i>{fmtShowTime(c.time)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="app-modal-hint">
                You can still save this gig and manage conflicts from your Gigs page.
              </p>
            </div>

            <div className="app-modal-footer">
              <button type="button" onClick={() => setConflictPending(null)} className="modal-btn modal-btn--cancel">
                <i className="bi bi-x-lg"></i>Cancel
              </button>
              <button type="button" onClick={handleConflictSaveAnyway} className="modal-btn modal-btn--confirm">
                <i className="bi bi-check-lg"></i>Save Anyway
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── TRIP LOCATION WARNING MODAL ── */}
      {tripWarning && (
        <div
          className="app-modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) setTripWarning(null) }}
        >
          <div className="trip-modal">

            <div className="app-modal-header">
              <div className="app-modal-header__left">
                <i className="bi bi-airplane-fill gig-icon-primary-lg"></i>
                <span className="app-modal-header__title">You're on a trip that day</span>
              </div>
              <button type="button" onClick={() => setTripWarning(null)} className="app-modal-header__close">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="app-modal-body">
              <div className="trip-warning-pill">
                <div className="trip-warning-pill__name-row">
                  <i className="bi bi-suitcase-fill gig-icon-primary-shrink"></i>
                  Trip: <strong>{tripWarning.tripLabel}</strong>
                </div>
                <div className="trip-warning-pill__dates">
                  {new Date(tripWarning.tripStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' to '}
                  {new Date(tripWarning.tripEnd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <p>
                This gig's date falls inside your <strong>{tripWarning.tripLabel}</strong> trip, but the
                venue city doesn't match your trip destination <strong>{tripWarning.tripDestination}</strong>.
              </p>
              <p className="app-modal-hint">
                Double-check the date and venue city before saving. You can still save if everything looks right.
              </p>
            </div>

            <div className="app-modal-footer">
              <button type="button" onClick={() => setTripWarning(null)} className="modal-btn modal-btn--cancel">
                <i className="bi bi-x-lg"></i>Cancel
              </button>
              <button type="button" onClick={handleTripSaveAnyway} className="modal-btn modal-btn--confirm">
                <i className="bi bi-check-lg"></i>Save Anyway
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── TRIP NUDGE MODAL ── */}
      {/* Shown after saving a new gig whose venue is 1.5+ hours from home. */}
      <TripNudgeModal
        open={!!tripNudge}
        gigName={tripNudge?.gigName}
        venueCity={tripNudge?.venueCity}
        onClose={() => { setTripNudge(null); navigate('/gigs') }}
        onPlanTrip={() => { setTripNudge(null); navigate('/calendar') }}
      />

    </div>
  )
}


// ============================================================
// TripNudgeModal
// ============================================================
// Defined here rather than a separate file so no import is needed.
// Shown after saving a new gig in a different state from home.
//
// Props:
//   open        boolean
//   onClose     fn()   - dismiss and go to /gigs
//   onPlanTrip  fn()   - go to /calendar
//   gigName     string
//   venueCity   string

function TripNudgeModal({ open, onClose, onPlanTrip, gigName, venueCity }) {
  if (!open) return null

  return (
    <div
      className="app-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="trip-modal trip-modal--narrow">

        <div className="app-modal-header">
          <div className="app-modal-header__left">
            <i className="bi bi-suitcase-fill gig-icon-primary-lg"></i>
            <span className="app-modal-header__title">Heads up - you're traveling!</span>
          </div>
          <button onClick={onClose} className="app-modal-header__close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="app-modal-body">
          <div className="trip-nudge-pill">
            <i className="bi bi-geo-alt-fill trip-nudge-pill__icon"></i>
            <div>
              <div className="trip-nudge-pill__gig-name">
                {gigName || 'This gig'} is in{' '}
                <strong className="trip-nudge-pill__city">
                  {venueCity || 'another state'}
                </strong>
              </div>
              <div className="trip-nudge-pill__sub">That's outside your home area.</div>
            </div>
          </div>
          <p>
            Want to set up a trip so the app can route you from your hotel,
            track your travel times, and send leave-by reminders while you're away?
          </p>
        </div>

        <div className="app-modal-footer">
          <button onClick={onClose} className="modal-btn modal-btn--cancel">
            <i className="bi bi-x-lg"></i>Skip for now
          </button>
          <button onClick={onPlanTrip} className="modal-btn modal-btn--plan">
            <i className="bi bi-suitcase-fill"></i>Plan a Trip
          </button>
        </div>

      </div>
    </div>
  )
}